// ============================================
// IMPORTS
// ============================================
import * as THREE from 'three';
import { initAuth, checkAccountPromptTrigger, isUserLoggedIn, maybeShowAccountPrompt } from './auth.js';
import { initStripe, isProductionMode, processPayment as processStripePayment, validateCardForm, requestRestoreCode, confirmRestoreCode } from './payment.js';
import { isSupabaseConfigured, getSavedProjects, saveProject as saveProjectToCloud, loadProject as loadProjectFromCloud, deleteProject as deleteProjectFromCloud } from './supabase.js';
import { setupGlobalErrorHandler, setupNetworkMonitoring, getUserFriendlyError, setButtonLoading } from './utils.js';

// ============================================
// DATA: Furniture Database
// ============================================
const FURNITURE_DATABASE = [
    // Bedroom
    { id: 'king_bed', name: 'King Bed', emoji: '🛏️', category: 'bedroom', width: 6.5, height: 2.5, depth: 7 },
    { id: 'queen_bed', name: 'Queen Bed', emoji: '🛏️', category: 'bedroom', width: 5, height: 2.5, depth: 6.5 },
    { id: 'twin_bed', name: 'Twin Bed', emoji: '🛏️', category: 'bedroom', width: 3.5, height: 2.5, depth: 6.5 },
    { id: 'dresser', name: 'Dresser', emoji: '🗄️', category: 'bedroom', width: 5, height: 3, depth: 1.5 },
    { id: 'nightstand', name: 'Nightstand', emoji: '🛋️', category: 'bedroom', width: 2, height: 2, depth: 1.5 },
    { id: 'wardrobe', name: 'Wardrobe', emoji: '🚪', category: 'bedroom', width: 4, height: 6.5, depth: 2 },
    { id: 'mattress_king', name: 'King Mattress', emoji: '🛏️', category: 'bedroom', width: 6.5, height: 1, depth: 6.5 },
    { id: 'mattress_queen', name: 'Queen Mattress', emoji: '🛏️', category: 'bedroom', width: 5, height: 1, depth: 6.5 },
    { id: 'box_spring', name: 'Box Spring', emoji: '📦', category: 'bedroom', width: 5, height: 0.75, depth: 6.5 },
    
    // Living Room
    { id: 'sofa_3seat', name: '3-Seat Sofa', emoji: '🛋️', category: 'living', width: 7, height: 3, depth: 3 },
    { id: 'sofa_2seat', name: 'Loveseat', emoji: '🛋️', category: 'living', width: 5, height: 3, depth: 3 },
    { id: 'sectional', name: 'Sectional', emoji: '🛋️', category: 'living', width: 9, height: 3, depth: 3.5 },
    { id: 'armchair', name: 'Armchair', emoji: '🪑', category: 'living', width: 3, height: 3, depth: 3 },
    { id: 'recliner', name: 'Recliner', emoji: '🪑', category: 'living', width: 3, height: 3.5, depth: 3 },
    { id: 'coffee_table', name: 'Coffee Table', emoji: '🪵', category: 'living', width: 4, height: 1.5, depth: 2 },
    { id: 'tv_stand', name: 'TV Stand', emoji: '📺', category: 'living', width: 5, height: 2, depth: 1.5 },
    { id: 'bookshelf', name: 'Bookshelf', emoji: '📚', category: 'living', width: 3, height: 6, depth: 1 },
    { id: 'end_table', name: 'End Table', emoji: '🪵', category: 'living', width: 2, height: 2, depth: 2 },
    
    // Dining
    { id: 'dining_table_6', name: '6-Person Table', emoji: '🪑', category: 'dining', width: 6, height: 2.5, depth: 3.5 },
    { id: 'dining_table_4', name: '4-Person Table', emoji: '🪑', category: 'dining', width: 4, height: 2.5, depth: 3 },
    { id: 'dining_chair', name: 'Dining Chair', emoji: '🪑', category: 'dining', width: 1.5, height: 3, depth: 1.5 },
    { id: 'china_cabinet', name: 'China Cabinet', emoji: '🗄️', category: 'dining', width: 4, height: 6.5, depth: 1.5 },
    { id: 'buffet', name: 'Buffet/Sideboard', emoji: '🗄️', category: 'dining', width: 5, height: 3, depth: 1.5 },
    { id: 'bar_stool', name: 'Bar Stool', emoji: '🪑', category: 'dining', width: 1.5, height: 3.5, depth: 1.5 },
    
    // Office
    { id: 'desk', name: 'Office Desk', emoji: '🖥️', category: 'office', width: 5, height: 2.5, depth: 2.5 },
    { id: 'desk_small', name: 'Small Desk', emoji: '🖥️', category: 'office', width: 4, height: 2.5, depth: 2 },
    { id: 'office_chair', name: 'Office Chair', emoji: '🪑', category: 'office', width: 2, height: 3.5, depth: 2 },
    { id: 'filing_cabinet', name: 'Filing Cabinet', emoji: '🗄️', category: 'office', width: 1.5, height: 4.5, depth: 2 },
    { id: 'bookcase', name: 'Bookcase', emoji: '📚', category: 'office', width: 3, height: 6, depth: 1 },
    
    // Appliances
    { id: 'fridge', name: 'Refrigerator', emoji: '🧊', category: 'appliance', width: 3, height: 6, depth: 3 },
    { id: 'washer', name: 'Washer', emoji: '🫧', category: 'appliance', width: 2.5, height: 3.5, depth: 2.5 },
    { id: 'dryer', name: 'Dryer', emoji: '💨', category: 'appliance', width: 2.5, height: 3.5, depth: 2.5 },
    { id: 'dishwasher', name: 'Dishwasher', emoji: '🍽️', category: 'appliance', width: 2, height: 3, depth: 2 },
    { id: 'microwave', name: 'Microwave', emoji: '📡', category: 'appliance', width: 1.5, height: 1, depth: 1.5 },
    { id: 'tv_65', name: '65" TV (boxed)', emoji: '📺', category: 'appliance', width: 5, height: 3, depth: 0.5 },
    { id: 'tv_55', name: '55" TV (boxed)', emoji: '📺', category: 'appliance', width: 4, height: 2.5, depth: 0.5 },
    { id: 'stacked_wd', name: 'Stacked W/D', emoji: '🫧', category: 'appliance', width: 2.5, height: 6.5, depth: 2.5 },

    // Boxes
    { id: 'box_small', name: 'Small Box', emoji: '📦', category: 'boxes', width: 1.5, height: 1, depth: 1 },
    { id: 'box_medium', name: 'Medium Box', emoji: '📦', category: 'boxes', width: 2, height: 1.5, depth: 1.5 },
    { id: 'box_large', name: 'Large Box', emoji: '📦', category: 'boxes', width: 2, height: 2, depth: 2 },
    { id: 'box_wardrobe', name: 'Wardrobe Box', emoji: '📦', category: 'boxes', width: 2, height: 4, depth: 2 },

    // Misc
    { id: 'lamp_floor', name: 'Floor Lamp', emoji: '💡', category: 'living', width: 1, height: 5, depth: 1 },
    { id: 'mirror_full', name: 'Full Mirror', emoji: '🪞', category: 'bedroom', width: 2, height: 5, depth: 0.5 },
    { id: 'rug_area', name: 'Area Rug (rolled)', emoji: '🧶', category: 'living', width: 1, height: 1, depth: 6 },
    { id: 'exercise_bike', name: 'Exercise Bike', emoji: '🚴', category: 'living', width: 2, height: 4, depth: 3.5 },
    { id: 'bicycle', name: 'Bicycle', emoji: '🚲', category: 'living', width: 2, height: 3.5, depth: 5.5 },
    { id: 'stroller', name: 'Stroller', emoji: '👶', category: 'living', width: 2, height: 3.5, depth: 3 },
];

// ============================================
// DATA: Container Templates
// ============================================
const CONTAINER_TEMPLATES = [
    // PODS
    { id: 'pods_7', name: 'PODS 7ft', icon: '📦', length: 7, width: 7, height: 8, brand: 'PODS' },
    { id: 'pods_12', name: 'PODS 12ft', icon: '📦', length: 12, width: 8, height: 8, brand: 'PODS' },
    { id: 'pods_16', name: 'PODS 16ft', icon: '📦', length: 16, width: 8, height: 8, brand: 'PODS' },
    
    // U-Haul Trucks
    { id: 'uhaul_10', name: 'U-Haul 10ft', icon: '🚛', length: 10, width: 6, height: 6, brand: 'U-Haul' },
    { id: 'uhaul_15', name: 'U-Haul 15ft', icon: '🚛', length: 15, width: 7.5, height: 7, brand: 'U-Haul' },
    { id: 'uhaul_20', name: 'U-Haul 20ft', icon: '🚛', length: 20, width: 7.5, height: 7.5, brand: 'U-Haul' },
    { id: 'uhaul_26', name: 'U-Haul 26ft', icon: '🚛', length: 26, width: 8, height: 8, brand: 'U-Haul' },
    
    // Storage Units
    { id: 'storage_5x5', name: '5×5 Storage', icon: '🏢', length: 5, width: 5, height: 8, brand: 'Storage' },
    { id: 'storage_5x10', name: '5×10 Storage', icon: '🏢', length: 10, width: 5, height: 8, brand: 'Storage' },
    { id: 'storage_10x10', name: '10×10 Storage', icon: '🏢', length: 10, width: 10, height: 8, brand: 'Storage' },
    { id: 'storage_10x15', name: '10×15 Storage', icon: '🏢', length: 15, width: 10, height: 8, brand: 'Storage' },
    { id: 'storage_10x20', name: '10×20 Storage', icon: '🏢', length: 20, width: 10, height: 8, brand: 'Storage' },
    
    // Cargo Vans
    { id: 'cargo_van', name: 'Cargo Van', icon: '🚐', length: 10, width: 5.5, height: 5, brand: 'Van' },
    { id: 'sprinter', name: 'Sprinter Van', icon: '🚐', length: 12, width: 5.5, height: 6, brand: 'Van' },
    
    // Trailers
    { id: 'trailer_6x12', name: '6×12 Trailer', icon: '🚚', length: 12, width: 6, height: 6, brand: 'Trailer' },
    
    // Cars - Cargo Area Dimensions (length x width x height of usable cargo space)
    { id: 'car_sedan', name: 'Sedan Trunk', icon: '🚗', length: 3.5, width: 4, height: 1.5, brand: 'Car' },
    { id: 'car_compact_suv', name: 'Compact SUV', icon: '🚙', length: 5, width: 4, height: 2.5, brand: 'Car' },
    { id: 'car_midsize_suv', name: 'Midsize SUV', icon: '🚙', length: 6, width: 4.5, height: 3, brand: 'Car' },
    { id: 'car_full_suv', name: 'Full-Size SUV', icon: '🚙', length: 7, width: 5, height: 3.5, brand: 'Car' },
    { id: 'car_minivan', name: 'Minivan', icon: '🚐', length: 8, width: 5, height: 4, brand: 'Car' },
    // Pickup beds: wall height ~1.5-2ft, but practical stacking height is 4-5ft
    { id: 'car_pickup_short', name: 'Pickup (5.5ft)', icon: '🛻', length: 5.5, width: 4.5, height: 5, brand: 'Car' },
    { id: 'car_pickup_standard', name: 'Pickup (6.5ft)', icon: '🛻', length: 6.5, width: 4.5, height: 5, brand: 'Car' },
    { id: 'car_pickup_long', name: 'Pickup (8ft)', icon: '🛻', length: 8, width: 4.5, height: 5, brand: 'Car' },
];

// ============================================
// STATE
// ============================================
let scene, camera, renderer, raycaster, mouse;
let container = CONTAINER_TEMPLATES[2]; // Default: PODS 16ft
let containerMesh;
let furnitureObjects = [];
let selectedObject = null;
let isDragging = false;
let dragPlane;
let currentCategory = 'all';
let currentContainerCategory = 'all';
let searchQuery = '';
let snapToGrid = false;
const GRID_SIZE = 0.5;

// Undo history
let undoHistory = [];
const MAX_UNDO_HISTORY = 20;
let selectedSaveSlot = 0;

// Escape user-controlled strings before interpolating into innerHTML
// (item names, project names, and search text can contain markup)
function escapeHtml(value) {
    return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

// ============================================
// FREEMIUM CONFIGURATION
// ============================================

// Check if Pro is active (handles expiration)
function checkProStatus() {
    const proData = localStorage.getItem('willitfit_pro_data');
    if (proData) {
        try {
            const data = JSON.parse(proData);
            // Check if plan has expired
            if (data.expiresAt) {
                const expiresAt = new Date(data.expiresAt);
                if (expiresAt > new Date()) {
                    return true; // Still valid
                }
                // Expired - clear pro status
                localStorage.removeItem('willitfit_pro_data');
                localStorage.removeItem('willitfit_pro');
                return false;
            }
            // No expiration = legacy lifetime
            return true;
        } catch {
            return false;
        }
    }
    // Fallback for old localStorage format
    return localStorage.getItem('willitfit_pro') === 'true';
}

// Get Pro plan details
function getProPlanDetails() {
    const proData = localStorage.getItem('willitfit_pro_data');
    if (proData) {
        try {
            const data = JSON.parse(proData);
            if (data.expiresAt) {
                const expiresAt = new Date(data.expiresAt);
                const now = new Date();
                const daysRemaining = Math.ceil((expiresAt - now) / (1000 * 60 * 60 * 24));
                return {
                    ...data,
                    daysRemaining,
                    isExpired: daysRemaining <= 0,
                };
            }
            return { ...data, daysRemaining: null, isExpired: false };
        } catch {
            return null;
        }
    }
    return null;
}

let isPro = checkProStatus();

const FREE_LIMITS = {
    maxItems: 15,
    maxSaveSlots: 1,
    maxUndos: 3,
    allowCustomItems: true,
    allowedContainers: ['pods_7', 'pods_12', 'pods_16', 'uhaul_10', 'uhaul_15', 'uhaul_20', 'uhaul_26'],
    allowedFurniture: [
        'king_bed', 'queen_bed', 'twin_bed', 'dresser', 'nightstand', 'mattress_queen',
        'sofa_3seat', 'sofa_2seat', 'armchair', 'coffee_table', 'tv_stand', 'bookshelf', 'end_table', 'lamp_floor',
        'dining_table_6', 'dining_table_4', 'dining_chair',
        'desk', 'office_chair',
        'box_small', 'box_medium', 'box_large', 'box_wardrobe'
    ]
};

const PRO_FEATURES = [
    { icon: '📦', title: 'Unlimited Items', desc: 'Add as many items as you need' },
    { icon: '🚗', title: 'All 24 Containers', desc: 'Cars, vans, storage units & more' },
    { icon: '🪑', title: 'Full Furniture Library', desc: '37+ items with realistic shapes' },
    { icon: '⚠️', title: 'Collision Detection', desc: 'See when items overlap' },
    { icon: '📋', title: 'Duplicate Items', desc: 'Quickly copy furniture pieces' },
    { icon: '💾', title: '3 Save Slots', desc: 'Save multiple packing plans' },
    { icon: '↩️', title: 'Unlimited Undo', desc: 'Never lose your progress' },
    { icon: '📤', title: 'Clean Exports', desc: 'No watermarks on your plans' }
];

// Colors for furniture — muted earth tones from the Daylight palette
const COLORS = [
    0xc98a6b, 0x5f93a0, 0xb89a55, 0x8a90ad, 0x86a087,
    0xbf9c72, 0xa97f8d, 0x7f9a6d, 0xc4795a, 0x6d86a8
];

// ============================================
// INITIALIZATION
// ============================================
function init() {
    // Setup error handling and network monitoring first
    setupGlobalErrorHandler();
    setupNetworkMonitoring();

    initScene();
    initUI();
    animate();

    // Hide loading overlay once scene is ready
    const loadingOverlay = document.getElementById('sceneLoadingOverlay');
    if (loadingOverlay) {
        loadingOverlay.classList.add('hidden');
        setTimeout(() => loadingOverlay.remove(), 400);
    }

    // Setup freemium UI
    updateProUI();

    // Initialize authentication
    initAuth();

    // Save initial empty state for undo
    saveState();

    // Show onboarding for first-time users
    setTimeout(showOnboarding, 500);

    console.log('Will It Fit? initialized successfully');
}

function initScene() {
    const simulatorDiv = document.getElementById('simulator');
    
    // Scene
    scene = new THREE.Scene();
    scene.background = new THREE.Color(0xede7db);
    
    // Camera
    camera = new THREE.PerspectiveCamera(50, simulatorDiv.clientWidth / simulatorDiv.clientHeight, 0.1, 1000);
    updateCameraPosition();
    
    // Renderer
    renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(simulatorDiv.clientWidth, simulatorDiv.clientHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    simulatorDiv.appendChild(renderer.domElement);
    
    // Lighting
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.4);
    scene.add(ambientLight);
    
    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
    directionalLight.position.set(30, 50, 30);
    directionalLight.castShadow = true;
    directionalLight.shadow.mapSize.width = 2048;
    directionalLight.shadow.mapSize.height = 2048;
    directionalLight.shadow.camera.near = 0.5;
    directionalLight.shadow.camera.far = 200;
    directionalLight.shadow.camera.left = -30;
    directionalLight.shadow.camera.right = 30;
    directionalLight.shadow.camera.top = 30;
    directionalLight.shadow.camera.bottom = -30;
    scene.add(directionalLight);
    
    const fillLight = new THREE.DirectionalLight(0xf0e6d2, 0.3);
    fillLight.position.set(-20, 20, -20);
    scene.add(fillLight);
    
    // Grid
    const gridHelper = new THREE.GridHelper(50, 50, 0xcfc5b3, 0xddd4c3);
    gridHelper.position.y = -0.01;
    scene.add(gridHelper);
    
    // Raycaster
    raycaster = new THREE.Raycaster();
    mouse = new THREE.Vector2();
    
    // Create container
    createContainer();
    
    // Drag plane
    dragPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
    
    // Event listeners
    setupEventListeners();
    
    // Handle resize
    window.addEventListener('resize', handleResize);
    handleResize();
}

function updateCameraPosition() {
    const maxDim = Math.max(container.length, container.width);
    const distance = maxDim * 1.8;
    camera.position.set(distance, distance * 0.8, distance);
    camera.lookAt(0, container.height / 4, 0);
}

function snapValue(value) {
    return snapToGrid ? Math.round(value / GRID_SIZE) * GRID_SIZE : value;
}

function toggleSnapToGrid(enabled) {
    snapToGrid = enabled;
    showToast(enabled ? 'Snap to grid enabled' : 'Snap to grid disabled', 'success');
}

function resetCamera() {
    updateCameraPosition();
    showToast('Camera reset', 'success');
}

function createContainer() {
    // Remove existing container
    if (containerMesh) {
        scene.remove(containerMesh);
    }
    
    const group = new THREE.Group();
    
    // Floor
    const floorGeometry = new THREE.PlaneGeometry(container.length, container.width);
    const floorMaterial = new THREE.MeshLambertMaterial({
        color: 0xbfd3d2,
        side: THREE.DoubleSide
    });
    const floor = new THREE.Mesh(floorGeometry, floorMaterial);
    floor.rotation.x = -Math.PI / 2;
    floor.receiveShadow = true;
    group.add(floor);
    
    // Walls (wireframe)
    const wallMaterial = new THREE.LineBasicMaterial({ color: 0x6b5c49, transparent: true, opacity: 0.55 });
    
    // Create box edges
    const boxGeometry = new THREE.BoxGeometry(container.length, container.height, container.width);
    const edges = new THREE.EdgesGeometry(boxGeometry);
    const wireframe = new THREE.LineSegments(edges, wallMaterial);
    wireframe.position.y = container.height / 2;
    group.add(wireframe);
    
    // Semi-transparent walls
    const wallGeometry = new THREE.BoxGeometry(container.length, container.height, container.width);
    const transparentMaterial = new THREE.MeshBasicMaterial({
        color: 0x6b5c49,
        transparent: true,
        opacity: 0.05,
        side: THREE.BackSide
    });
    const walls = new THREE.Mesh(wallGeometry, transparentMaterial);
    walls.position.y = container.height / 2;
    group.add(walls);
    
    containerMesh = group;
    scene.add(group);
    
    // Update camera
    updateCameraPosition();
    
    // Update stats display
    updateStats();
}

function initUI() {
    // Render furniture grid
    renderFurnitureGrid();
    
    // Render container grid
    renderContainerGrid();
    
    // Tab switching
    document.querySelectorAll('.tab').forEach(tab => {
        tab.addEventListener('click', () => {
            document.querySelectorAll('.tab').forEach(t => t.classList.remove('active'));
            document.querySelectorAll('.tab-content').forEach(c => c.classList.remove('active'));
            tab.classList.add('active');
            document.getElementById(`tab-${tab.dataset.tab}`).classList.add('active');
        });
    });
    
    // Furniture category filtering
    document.querySelectorAll('.category-btn[data-category]').forEach(btn => {
        btn.addEventListener('click', () => {
            document.querySelectorAll('.category-btn[data-category]').forEach(b => b.classList.remove('active'));
            btn.classList.add('active');
            currentCategory = btn.dataset.category;
            renderFurnitureGrid();
        });
    });
    
    // Container category filtering
    document.querySelectorAll('.category-btn[data-container-category]').forEach(btn => {
        btn.addEventListener('click', () => {
            document.querySelectorAll('.category-btn[data-container-category]').forEach(b => b.classList.remove('active'));
            btn.classList.add('active');
            currentContainerCategory = btn.dataset.containerCategory;
            renderContainerGrid();
        });
    });
}

function renderFurnitureGrid() {
    const grid = document.getElementById('furnitureGrid');
    let filtered = currentCategory === 'all' 
        ? FURNITURE_DATABASE 
        : FURNITURE_DATABASE.filter(f => f.category === currentCategory);
    
    // Apply search filter
    if (searchQuery) {
        const query = searchQuery.toLowerCase();
        filtered = filtered.filter(f => 
            f.name.toLowerCase().includes(query) || 
            f.category.toLowerCase().includes(query)
        );
    }
    
    if (filtered.length === 0) {
        grid.innerHTML = `<div class="empty-state" style="grid-column: 1/-1;"><p>No furniture found matching "${escapeHtml(searchQuery)}"</p></div>`;
        return;
    }
    
    grid.innerHTML = filtered.map(item => {
        const isLocked = !isPro && !FREE_LIMITS.allowedFurniture.includes(item.id);
        const lockedClass = isLocked ? 'locked' : '';
        const clickHandler = isLocked 
            ? `showPaywall('Locked Furniture', '${item.name} is a Pro item. Upgrade to access all 37+ furniture pieces.')`
            : `addFurnitureFromDatabase('${item.id}')`;
        
        return `
            <div class="furniture-item ${lockedClass}" onclick="${clickHandler}">
                ${isLocked ? '<div class="pro-badge">PRO</div>' : ''}
                <div class="emoji">${item.emoji}</div>
                <div class="name">${item.name}</div>
                <div class="dims">${item.width}×${item.height}×${item.depth}ft</div>
                ${isLocked ? '<div class="locked-overlay"><span class="lock-icon">🔒 PRO</span></div>' : ''}
            </div>
        `;
    }).join('');
}

function filterFurniture() {
    searchQuery = document.getElementById('furnitureSearch').value;
    renderFurnitureGrid();
}

function renderContainerGrid() {
    const grid = document.getElementById('containerGrid');
    const filtered = currentContainerCategory === 'all' 
        ? CONTAINER_TEMPLATES 
        : CONTAINER_TEMPLATES.filter(c => c.brand === currentContainerCategory);
    
    grid.innerHTML = filtered.map(c => {
        const volume = c.length * c.width * c.height;
        const selected = c.id === container.id ? 'selected' : '';
        const isLocked = !isPro && !FREE_LIMITS.allowedContainers.includes(c.id);
        const lockedClass = isLocked ? 'locked' : '';
        const clickHandler = isLocked 
            ? `showPaywall('Locked Container', '${c.name} is a Pro container. Upgrade to access all 24 containers including cars, storage units, and vans.')`
            : `selectContainer('${c.id}')`;
        
        return `
            <div class="container-option ${selected} ${lockedClass}" onclick="${clickHandler}">
                ${isLocked ? '<div class="pro-badge">PRO</div>' : ''}
                <div class="icon">${c.icon}</div>
                <div class="name">${c.name}</div>
                <div class="size">${c.length}×${c.width}×${c.height}ft</div>
                ${isLocked ? '<div class="locked-overlay"><span class="lock-icon">🔒 PRO</span></div>' : ''}
            </div>
        `;
    }).join('');
}

// ============================================
// EVENT HANDLERS
// ============================================
function setupEventListeners() {
    const canvas = renderer.domElement;
    
    // Mouse events
    canvas.addEventListener('mousedown', onMouseDown);
    canvas.addEventListener('mousemove', onMouseMove);
    canvas.addEventListener('mouseup', onMouseUp);
    canvas.addEventListener('wheel', onMouseWheel);
    
    // Touch events
    canvas.addEventListener('touchstart', onTouchStart, { passive: false });
    canvas.addEventListener('touchmove', onTouchMove, { passive: false });
    canvas.addEventListener('touchend', onTouchEnd);
    
    // Suppress right-click context menu on canvas
    canvas.addEventListener('contextmenu', (e) => e.preventDefault());

    // Keyboard events
    document.addEventListener('keydown', onKeyDown);
}

let isOrbiting = false;
let lastMouseX = 0, lastMouseY = 0;

function onMouseDown(event) {
    event.preventDefault();
    updateMouse(event);
    
    if (event.button === 0) { // Left click
        const intersects = getIntersects();
        if (intersects.length > 0) {
            const hitObject = intersects[0].object;
            const furniture = getParentFurniture(hitObject);
            if (furniture) {
                if (event.shiftKey) {
                    // Multi-select with shift
                    toggleMultiSelect(furniture);
                    return;
                }
                // Clear multi-select on normal click
                clearMultiSelect();
                selectObject(furniture);
                isDragging = true;
                return;
            }
        }
        // Clear multi-select when clicking empty space
        if (!event.shiftKey && selectedObjects.size > 0) {
            clearMultiSelect();
        }
        // Start orbiting if no furniture clicked
        isOrbiting = true;
    } else if (event.button === 2) {
        isOrbiting = true;
    }
    
    lastMouseX = event.clientX;
    lastMouseY = event.clientY;
}

function onMouseMove(event) {
    event.preventDefault();
    updateMouse(event);
    
    if (isDragging && selectedObject) {
        const planeIntersect = new THREE.Vector3();
        raycaster.ray.intersectPlane(dragPlane, planeIntersect);

        if (planeIntersect) {
            // Get object dimensions from bounding box
            const box = new THREE.Box3().setFromObject(selectedObject);
            const size = new THREE.Vector3();
            box.getSize(size);

            // Constrain to container bounds
            const halfLength = container.length / 2 - size.x / 2;
            const halfWidth = container.width / 2 - size.z / 2;

            let newX = planeIntersect.x;
            let newZ = planeIntersect.z;
            newX = snapValue(newX);
            newZ = snapValue(newZ);

            selectedObject.position.x = Math.max(-halfLength, Math.min(halfLength, newX));
            selectedObject.position.z = Math.max(-halfWidth, Math.min(halfWidth, newZ));
        }
    } else if (isOrbiting) {
        const deltaX = event.clientX - lastMouseX;
        const deltaY = event.clientY - lastMouseY;
        
        // Orbit camera around origin
        const spherical = new THREE.Spherical();
        spherical.setFromVector3(camera.position);
        spherical.theta -= deltaX * 0.01;
        spherical.phi = Math.max(0.1, Math.min(Math.PI / 2 - 0.1, spherical.phi + deltaY * 0.01));
        camera.position.setFromSpherical(spherical);
        camera.lookAt(0, container.height / 4, 0);
        
        lastMouseX = event.clientX;
        lastMouseY = event.clientY;
    }
}

function onMouseUp(event) {
    if (isDragging && selectedObject) {
        saveState();
        checkCollisions();
    }
    isDragging = false;
    isOrbiting = false;
}

function onMouseWheel(event) {
    event.preventDefault();
    const zoomSpeed = 0.001;
    const distance = camera.position.length();
    const newDistance = distance * (1 + event.deltaY * zoomSpeed);
    const clampedDistance = Math.max(10, Math.min(80, newDistance));
    camera.position.normalize().multiplyScalar(clampedDistance);
}

// Touch handling
let touchStartDistance = 0;
let lastTouchX = 0, lastTouchY = 0;

function onTouchStart(event) {
    event.preventDefault();
    
    if (event.touches.length === 1) {
        const touch = event.touches[0];
        updateMouseFromTouch(touch);
        
        const intersects = getIntersects();
        if (intersects.length > 0) {
            const hitObject = intersects[0].object;
            const furniture = getParentFurniture(hitObject);
            if (furniture) {
                selectObject(furniture);
                isDragging = true;
                return;
            }
        }
        
        isOrbiting = true;
        lastTouchX = touch.clientX;
        lastTouchY = touch.clientY;
    } else if (event.touches.length === 2) {
        const dx = event.touches[0].clientX - event.touches[1].clientX;
        const dy = event.touches[0].clientY - event.touches[1].clientY;
        touchStartDistance = Math.sqrt(dx * dx + dy * dy);
    }
}

function onTouchMove(event) {
    event.preventDefault();
    
    if (event.touches.length === 1) {
        const touch = event.touches[0];
        updateMouseFromTouch(touch);
        
        if (isDragging && selectedObject) {
            const planeIntersect = new THREE.Vector3();
            raycaster.ray.intersectPlane(dragPlane, planeIntersect);

            if (planeIntersect) {
                const box = new THREE.Box3().setFromObject(selectedObject);
                const size = new THREE.Vector3();
                box.getSize(size);

                const halfLength = container.length / 2 - size.x / 2;
                const halfWidth = container.width / 2 - size.z / 2;

                let newX = snapValue(planeIntersect.x);
                let newZ = snapValue(planeIntersect.z);

                selectedObject.position.x = Math.max(-halfLength, Math.min(halfLength, newX));
                selectedObject.position.z = Math.max(-halfWidth, Math.min(halfWidth, newZ));
            }
        } else if (isOrbiting) {
            const deltaX = touch.clientX - lastTouchX;
            const deltaY = touch.clientY - lastTouchY;
            
            const spherical = new THREE.Spherical();
            spherical.setFromVector3(camera.position);
            spherical.theta -= deltaX * 0.01;
            spherical.phi = Math.max(0.1, Math.min(Math.PI / 2 - 0.1, spherical.phi + deltaY * 0.01));
            camera.position.setFromSpherical(spherical);
            camera.lookAt(0, container.height / 4, 0);
            
            lastTouchX = touch.clientX;
            lastTouchY = touch.clientY;
        }
    } else if (event.touches.length === 2) {
        // Pinch to zoom
        const dx = event.touches[0].clientX - event.touches[1].clientX;
        const dy = event.touches[0].clientY - event.touches[1].clientY;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        const scale = touchStartDistance / distance;
        const currentDistance = camera.position.length();
        const newDistance = Math.max(10, Math.min(80, currentDistance * scale));
        camera.position.normalize().multiplyScalar(newDistance);
        
        touchStartDistance = distance;
    }
}

function onTouchEnd(event) {
    if (isDragging && selectedObject) {
        saveState();
        checkCollisions();
    }
    isDragging = false;
    isOrbiting = false;
}

function onKeyDown(event) {
    // Ctrl+Z undo works regardless of selection
    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'z') {
        event.preventDefault();
        undoAction();
        return;
    }

    if (!selectedObject) return;

    switch(event.key.toLowerCase()) {
        case 'r':
            rotateSelected();
            break;
        case 'f':
            flipSelected();
            break;
        case 'arrowup':
            event.preventDefault();
            moveSelectedUp();
            break;
        case 'arrowdown':
            event.preventDefault();
            moveSelectedDown();
            break;
        case 'delete':
        case 'backspace':
            deleteSelected();
            break;
    }
}

function updateMouse(event) {
    const rect = renderer.domElement.getBoundingClientRect();
    mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
    raycaster.setFromCamera(mouse, camera);
}

function updateMouseFromTouch(touch) {
    const rect = renderer.domElement.getBoundingClientRect();
    mouse.x = ((touch.clientX - rect.left) / rect.width) * 2 - 1;
    mouse.y = -((touch.clientY - rect.top) / rect.height) * 2 + 1;
    raycaster.setFromCamera(mouse, camera);
}

function getIntersects() {
    const allMeshes = [];
    furnitureObjects.forEach(group => {
        group.traverse(child => {
            if (child.isMesh) {
                allMeshes.push(child);
            }
        });
    });
    return raycaster.intersectObjects(allMeshes);
}

function getParentFurniture(object) {
    // If clicked object has a parent group reference, return it
    if (object.userData.parentGroup) {
        return object.userData.parentGroup;
    }
    // Otherwise traverse up to find the furniture group
    let current = object;
    while (current.parent && !current.userData.isFurniture) {
        current = current.parent;
    }
    return current.userData.isFurniture ? current : null;
}

function handleResize() {
    const simulatorDiv = document.getElementById('simulator');
    const width = simulatorDiv.clientWidth;
    const height = simulatorDiv.clientHeight;
    
    if (width > 0 && height > 0) {
        camera.aspect = width / height;
        camera.updateProjectionMatrix();
        renderer.setSize(width, height);
    }
}

// ============================================
// FURNITURE MANAGEMENT
// ============================================
function addFurnitureFromDatabase(id) {
    const item = FURNITURE_DATABASE.find(f => f.id === id);
    if (item) {
        addFurniture(item.name, item.width, item.height, item.depth, item.id);
    }
}

function addCustomItem() {
    const name = document.getElementById('customName').value || 'Custom Item';
    const width = parseFloat(document.getElementById('customWidth').value) || 3;
    const height = parseFloat(document.getElementById('customHeight').value) || 3;
    const depth = parseFloat(document.getElementById('customDepth').value) || 2;
    
    addFurniture(name, width, height, depth);
}

// ============================================
// FURNITURE SHAPE BUILDERS
// ============================================
function createChairShape(width, height, depth, material) {
    const group = new THREE.Group();
    
    const seatHeight = 1.5; // Seat height from ground
    const seatThickness = 0.3;
    const backThickness = 0.3;
    const legSize = 0.2;
    
    // Seat
    const seatGeom = new THREE.BoxGeometry(width, seatThickness, depth);
    const seat = new THREE.Mesh(seatGeom, material);
    seat.position.y = seatHeight;
    seat.castShadow = true;
    seat.receiveShadow = true;
    group.add(seat);
    
    // Back
    const backHeight = height - seatHeight - seatThickness / 2;
    const backGeom = new THREE.BoxGeometry(width, backHeight, backThickness);
    const back = new THREE.Mesh(backGeom, material);
    back.position.y = seatHeight + seatThickness / 2 + backHeight / 2;
    back.position.z = -depth / 2 + backThickness / 2;
    back.castShadow = true;
    back.receiveShadow = true;
    group.add(back);
    
    // Legs (4 corners)
    const legGeom = new THREE.BoxGeometry(legSize, seatHeight - seatThickness / 2, legSize);
    const legPositions = [
        { x: -width / 2 + legSize, z: -depth / 2 + legSize },
        { x: width / 2 - legSize, z: -depth / 2 + legSize },
        { x: -width / 2 + legSize, z: depth / 2 - legSize },
        { x: width / 2 - legSize, z: depth / 2 - legSize }
    ];
    
    legPositions.forEach(pos => {
        const leg = new THREE.Mesh(legGeom, material);
        leg.position.set(pos.x, (seatHeight - seatThickness / 2) / 2, pos.z);
        leg.castShadow = true;
        group.add(leg);
    });
    
    return group;
}

function createSofaShape(width, height, depth, material) {
    const group = new THREE.Group();
    
    const seatHeight = 1.5;
    const seatDepth = depth * 0.7;
    const armWidth = 0.5;
    const backHeight = height - seatHeight + 0.5;
    
    // Base/Seat
    const seatGeom = new THREE.BoxGeometry(width, seatHeight, seatDepth);
    const seat = new THREE.Mesh(seatGeom, material);
    seat.position.y = seatHeight / 2;
    seat.position.z = depth / 2 - seatDepth / 2;
    seat.castShadow = true;
    seat.receiveShadow = true;
    group.add(seat);
    
    // Back
    const backGeom = new THREE.BoxGeometry(width - armWidth * 2, backHeight, depth - seatDepth);
    const back = new THREE.Mesh(backGeom, material);
    back.position.y = seatHeight + backHeight / 2 - 0.5;
    back.position.z = -depth / 2 + (depth - seatDepth) / 2;
    back.castShadow = true;
    back.receiveShadow = true;
    group.add(back);
    
    // Arms
    const armGeom = new THREE.BoxGeometry(armWidth, height - 0.5, depth);
    
    const leftArm = new THREE.Mesh(armGeom, material);
    leftArm.position.set(-width / 2 + armWidth / 2, (height - 0.5) / 2, 0);
    leftArm.castShadow = true;
    group.add(leftArm);
    
    const rightArm = new THREE.Mesh(armGeom, material);
    rightArm.position.set(width / 2 - armWidth / 2, (height - 0.5) / 2, 0);
    rightArm.castShadow = true;
    group.add(rightArm);
    
    return group;
}

function createTableShape(width, height, depth, material) {
    const group = new THREE.Group();
    
    const topThickness = 0.3;
    const legSize = 0.25;
    
    // Table top
    const topGeom = new THREE.BoxGeometry(width, topThickness, depth);
    const top = new THREE.Mesh(topGeom, material);
    top.position.y = height - topThickness / 2;
    top.castShadow = true;
    top.receiveShadow = true;
    group.add(top);
    
    // Legs
    const legHeight = height - topThickness;
    const legGeom = new THREE.BoxGeometry(legSize, legHeight, legSize);
    const inset = 0.3;
    
    const legPositions = [
        { x: -width / 2 + inset, z: -depth / 2 + inset },
        { x: width / 2 - inset, z: -depth / 2 + inset },
        { x: -width / 2 + inset, z: depth / 2 - inset },
        { x: width / 2 - inset, z: depth / 2 - inset }
    ];
    
    legPositions.forEach(pos => {
        const leg = new THREE.Mesh(legGeom, material);
        leg.position.set(pos.x, legHeight / 2, pos.z);
        leg.castShadow = true;
        group.add(leg);
    });
    
    return group;
}

function createDeskShape(width, height, depth, material) {
    const group = new THREE.Group();
    
    const topThickness = 0.25;
    const legWidth = 0.5;
    const legDepth = depth - 0.3;
    
    // Desktop
    const topGeom = new THREE.BoxGeometry(width, topThickness, depth);
    const top = new THREE.Mesh(topGeom, material);
    top.position.y = height - topThickness / 2;
    top.castShadow = true;
    top.receiveShadow = true;
    group.add(top);
    
    // Side panels (like a typical desk)
    const panelHeight = height - topThickness;
    const panelGeom = new THREE.BoxGeometry(legWidth, panelHeight, legDepth);
    
    const leftPanel = new THREE.Mesh(panelGeom, material);
    leftPanel.position.set(-width / 2 + legWidth / 2, panelHeight / 2, 0);
    leftPanel.castShadow = true;
    group.add(leftPanel);
    
    const rightPanel = new THREE.Mesh(panelGeom, material);
    rightPanel.position.set(width / 2 - legWidth / 2, panelHeight / 2, 0);
    rightPanel.castShadow = true;
    group.add(rightPanel);
    
    return group;
}

function createBedShape(width, height, depth, material) {
    const group = new THREE.Group();
    
    const frameHeight = 1;
    const mattressHeight = 0.8;
    const headboardHeight = height - frameHeight;
    const headboardThickness = 0.4;
    
    // Frame/Base
    const frameGeom = new THREE.BoxGeometry(width, frameHeight, depth);
    const frame = new THREE.Mesh(frameGeom, material);
    frame.position.y = frameHeight / 2;
    frame.castShadow = true;
    frame.receiveShadow = true;
    group.add(frame);
    
    // Mattress (slightly different shade)
    const mattressMat = material.clone();
    mattressMat.color.offsetHSL(0, 0, 0.1);
    const mattressGeom = new THREE.BoxGeometry(width - 0.2, mattressHeight, depth - 0.2);
    const mattress = new THREE.Mesh(mattressGeom, mattressMat);
    mattress.position.y = frameHeight + mattressHeight / 2;
    mattress.castShadow = true;
    group.add(mattress);
    
    // Headboard
    const headGeom = new THREE.BoxGeometry(width, headboardHeight, headboardThickness);
    const headboard = new THREE.Mesh(headGeom, material);
    headboard.position.y = frameHeight + headboardHeight / 2;
    headboard.position.z = -depth / 2 + headboardThickness / 2;
    headboard.castShadow = true;
    group.add(headboard);
    
    return group;
}

function createBookshelfShape(width, height, depth, material) {
    const group = new THREE.Group();
    
    const shelfThickness = 0.15;
    const sideThickness = 0.2;
    const numShelves = Math.floor(height / 1.2);
    
    // Sides
    const sideGeom = new THREE.BoxGeometry(sideThickness, height, depth);
    
    const leftSide = new THREE.Mesh(sideGeom, material);
    leftSide.position.set(-width / 2 + sideThickness / 2, height / 2, 0);
    leftSide.castShadow = true;
    group.add(leftSide);
    
    const rightSide = new THREE.Mesh(sideGeom, material);
    rightSide.position.set(width / 2 - sideThickness / 2, height / 2, 0);
    rightSide.castShadow = true;
    group.add(rightSide);
    
    // Shelves
    const shelfGeom = new THREE.BoxGeometry(width - sideThickness * 2, shelfThickness, depth);
    
    for (let i = 0; i <= numShelves; i++) {
        const shelf = new THREE.Mesh(shelfGeom, material);
        const y = i === 0 ? shelfThickness / 2 : (i / numShelves) * (height - shelfThickness);
        shelf.position.y = y;
        shelf.castShadow = true;
        shelf.receiveShadow = true;
        group.add(shelf);
    }
    
    // Back panel
    const backGeom = new THREE.BoxGeometry(width - sideThickness * 2, height, 0.1);
    const backPanel = new THREE.Mesh(backGeom, material);
    backPanel.position.set(0, height / 2, -depth / 2 + 0.05);
    group.add(backPanel);
    
    return group;
}

function createArmchairShape(width, height, depth, material) {
    const group = new THREE.Group();
    
    const seatHeight = 1.5;
    const armHeight = 2;
    const armWidth = 0.4;
    const backThickness = 0.5;
    
    // Seat base
    const seatGeom = new THREE.BoxGeometry(width - armWidth * 2, seatHeight, depth - backThickness);
    const seat = new THREE.Mesh(seatGeom, material);
    seat.position.y = seatHeight / 2;
    seat.position.z = backThickness / 2;
    seat.castShadow = true;
    seat.receiveShadow = true;
    group.add(seat);
    
    // Back
    const backHeight = height - seatHeight * 0.3;
    const backGeom = new THREE.BoxGeometry(width, backHeight, backThickness);
    const back = new THREE.Mesh(backGeom, material);
    back.position.y = seatHeight * 0.3 + backHeight / 2;
    back.position.z = -depth / 2 + backThickness / 2;
    back.castShadow = true;
    group.add(back);
    
    // Arms
    const armGeom = new THREE.BoxGeometry(armWidth, armHeight, depth);
    
    const leftArm = new THREE.Mesh(armGeom, material);
    leftArm.position.set(-width / 2 + armWidth / 2, armHeight / 2, 0);
    leftArm.castShadow = true;
    group.add(leftArm);
    
    const rightArm = new THREE.Mesh(armGeom, material);
    rightArm.position.set(width / 2 - armWidth / 2, armHeight / 2, 0);
    rightArm.castShadow = true;
    group.add(rightArm);
    
    return group;
}

function createDresserShape(width, height, depth, material) {
    const group = new THREE.Group();
    
    // Main body
    const bodyGeom = new THREE.BoxGeometry(width, height - 0.3, depth);
    const body = new THREE.Mesh(bodyGeom, material);
    body.position.y = (height - 0.3) / 2 + 0.3;
    body.castShadow = true;
    body.receiveShadow = true;
    group.add(body);
    
    // Legs/base
    const legGeom = new THREE.BoxGeometry(width - 0.2, 0.3, depth - 0.2);
    const base = new THREE.Mesh(legGeom, material);
    base.position.y = 0.15;
    base.castShadow = true;
    group.add(base);
    
    // Drawer lines (visual detail)
    const numDrawers = Math.floor(height / 1);
    const drawerMat = material.clone();
    drawerMat.color.offsetHSL(0, 0, -0.1);
    
    for (let i = 0; i < numDrawers; i++) {
        const lineGeom = new THREE.BoxGeometry(width - 0.4, 0.05, 0.05);
        const line = new THREE.Mesh(lineGeom, drawerMat);
        line.position.set(0, 0.5 + i * 0.9, depth / 2 + 0.01);
        group.add(line);
    }
    
    return group;
}

function createBoxShape(width, height, depth, material) {
    // Simple box for items that don't need special shapes
    const geometry = new THREE.BoxGeometry(width, height, depth);
    const mesh = new THREE.Mesh(geometry, material);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    
    const group = new THREE.Group();
    group.add(mesh);
    mesh.position.y = height / 2;
    group.position.y = -height / 2; // Offset so group origin is at bottom
    
    return group;
}

function getShapeType(itemId, name) {
    // Determine shape type based on item ID or name
    const lowerName = name.toLowerCase();
    
    if (itemId) {
        if (itemId.includes('chair') || itemId.includes('stool')) return 'chair';
        if (itemId.includes('sofa') || itemId.includes('sectional') || itemId.includes('loveseat')) return 'sofa';
        if (itemId.includes('table') && !itemId.includes('nightstand') && !itemId.includes('end_table')) return 'table';
        if (itemId.includes('desk')) return 'desk';
        if (itemId.includes('bed') && !itemId.includes('mattress')) return 'bed';
        if (itemId.includes('bookshelf') || itemId.includes('bookcase')) return 'bookshelf';
        if (itemId.includes('armchair') || itemId.includes('recliner')) return 'armchair';
        if (itemId.includes('dresser') || itemId.includes('cabinet') || itemId.includes('wardrobe')) return 'dresser';
    }
    
    // Fallback to name matching
    if (lowerName.includes('chair') || lowerName.includes('stool')) return 'chair';
    if (lowerName.includes('sofa') || lowerName.includes('couch') || lowerName.includes('loveseat')) return 'sofa';
    if (lowerName.includes('table')) return 'table';
    if (lowerName.includes('desk')) return 'desk';
    if (lowerName.includes('bed')) return 'bed';
    if (lowerName.includes('shelf') || lowerName.includes('bookcase')) return 'bookshelf';
    if (lowerName.includes('armchair') || lowerName.includes('recliner')) return 'armchair';
    if (lowerName.includes('dresser') || lowerName.includes('cabinet')) return 'dresser';
    
    return 'box';
}

function createFurnitureShape(shapeType, width, height, depth, material) {
    switch (shapeType) {
        case 'chair': return createChairShape(width, height, depth, material);
        case 'sofa': return createSofaShape(width, height, depth, material);
        case 'table': return createTableShape(width, height, depth, material);
        case 'desk': return createDeskShape(width, height, depth, material);
        case 'bed': return createBedShape(width, height, depth, material);
        case 'bookshelf': return createBookshelfShape(width, height, depth, material);
        case 'armchair': return createArmchairShape(width, height, depth, material);
        case 'dresser': return createDresserShape(width, height, depth, material);
        default: return createBoxShape(width, height, depth, material);
    }
}

function addFurniture(name, width, height, depth, itemId = null) {
    // Check free tier limit
    if (!canAddItem()) {
        showPaywall('Item Limit Reached', `You've reached the free limit of ${FREE_LIMITS.maxItems} items. Upgrade to Pro for unlimited items!`);
        return;
    }
    
    // Check if it fits
    if (width > container.length || depth > container.width || height > container.height) {
        showToast('Item is too large for this container!', 'error');
        return;
    }
    
    // Create material
    const color = COLORS[furnitureObjects.length % COLORS.length];
    const material = new THREE.MeshLambertMaterial({ color: color });
    
    // Determine shape type and create appropriate shape
    const shapeType = getShapeType(itemId, name);
    const furnitureGroup = createFurnitureShape(shapeType, width, height, depth, material);
    
    // Position the group
    furnitureGroup.position.y = 0;
    furnitureGroup.position.x = -container.length / 2 + width / 2 + 0.5;
    furnitureGroup.position.z = -container.width / 2 + depth / 2 + 0.5;
    
    // Find non-overlapping position
    let attempts = 0;
    while (attempts < 50 && checkOverlap(furnitureGroup)) {
        furnitureGroup.position.x += 1;
        if (furnitureGroup.position.x > container.length / 2 - width / 2) {
            furnitureGroup.position.x = -container.length / 2 + width / 2 + 0.5;
            furnitureGroup.position.z += 1;
        }
        if (furnitureGroup.position.z > container.width / 2 - depth / 2) {
            furnitureGroup.position.z = -container.width / 2 + depth / 2 + 0.5;
            furnitureGroup.position.y += height;
        }
        attempts++;
    }
    
    // Store metadata on the group
    furnitureGroup.userData = {
        isFurniture: true,
        name: name,
        originalWidth: width,
        originalHeight: height,
        originalDepth: depth,
        color: color,
        volume: width * height * depth,
        shapeType: shapeType
    };
    
    // Mark all children as furniture for raycasting
    furnitureGroup.traverse(child => {
        if (child.isMesh) {
            child.userData.isFurniture = true;
            child.userData.parentGroup = furnitureGroup;
        }
    });
    
    scene.add(furnitureGroup);
    furnitureObjects.push(furnitureGroup);
    
    selectObject(furnitureGroup);
    updateItemsList();
    updateStats();
    updateItemLimitBar();
    checkCollisions();
    saveState();

    showToast(`Added ${name}`, 'success');

    // Show viewport/touch hints for first-time users
    showFirstItemHints();

    // Stay on current tab so user can keep adding items
}

function checkOverlap(newMesh) {
    const newBox = new THREE.Box3().setFromObject(newMesh);
    
    for (const obj of furnitureObjects) {
        const existingBox = new THREE.Box3().setFromObject(obj);
        if (newBox.intersectsBox(existingBox)) {
            return true;
        }
    }
    return false;
}

function selectObject(obj) {
    // Deselect previous
    if (selectedObject) {
        selectedObject.traverse(child => {
            if (child.isMesh && child.material) {
                child.material.emissive = new THREE.Color(0x000000);
            }
        });
        removeDimensionLabels();
    }
    
    selectedObject = obj;

    if (obj) {
        // Show dimension labels
        createDimensionLabels(obj);

        // Highlight all meshes in the group
        obj.traverse(child => {
            if (child.isMesh && child.material) {
                child.material.emissive = new THREE.Color(0x333333);
            }
        });
        
        // Update drag plane to object's base height
        const box = new THREE.Box3().setFromObject(obj);
        dragPlane.constant = -obj.position.y;
        
        // Populate edit fields with current dimensions
        document.getElementById('editName').value = obj.userData.name || '';
        document.getElementById('editWidth').value = obj.userData.originalWidth || 3;
        document.getElementById('editHeight').value = obj.userData.originalHeight || 3;
        document.getElementById('editDepth').value = obj.userData.originalDepth || 2;
        document.getElementById('selectedItemName').textContent = obj.userData.name || 'Selected Item';
        
        // Update volume preview
        updateVolumePreview();
        
        // Show controls
        document.getElementById('selectedControls').style.display = 'block';
    } else {
        document.getElementById('selectedControls').style.display = 'none';
        removeDimensionLabels();
    }

    updateItemsList();
}

function updateVolumePreview() {
    const width = parseFloat(document.getElementById('editWidth').value) || 0;
    const height = parseFloat(document.getElementById('editHeight').value) || 0;
    const depth = parseFloat(document.getElementById('editDepth').value) || 0;
    const volume = width * height * depth;
    
    document.getElementById('editVolumePreview').textContent = `Volume: ${volume.toFixed(1)} ft³`;
}

function rotateSelected() {
    if (!selectedObject) return;
    
    saveState();
    selectedObject.rotation.y += Math.PI / 2;
    
    // Recalculate bounds and constrain
    constrainToContainer(selectedObject);
    checkCollisions();
    showToast('Rotated 90°', 'success');
}

function applyDimensionChanges() {
    if (!selectedObject) return;
    
    const newName = document.getElementById('editName').value || 'Custom Item';
    const newWidth = parseFloat(document.getElementById('editWidth').value) || 3;
    const newHeight = parseFloat(document.getElementById('editHeight').value) || 3;
    const newDepth = parseFloat(document.getElementById('editDepth').value) || 2;
    
    // Check if new dimensions fit in container
    if (newWidth > container.length || newDepth > container.width || newHeight > container.height) {
        showToast('New dimensions are too large for this container!', 'error');
        return;
    }
    
    saveState();
    
    // Store current position and rotation
    const currentPosition = selectedObject.position.clone();
    const currentRotation = selectedObject.rotation.clone();
    const currentColor = selectedObject.userData.color;
    const shapeType = selectedObject.userData.shapeType || getShapeType(null, newName);
    
    // Find index in array
    const index = furnitureObjects.indexOf(selectedObject);
    
    // Remove old object from scene
    scene.remove(selectedObject);
    
    // Create new material with same color
    const material = new THREE.MeshLambertMaterial({ color: currentColor });
    
    // Create new furniture with updated dimensions
    const newFurnitureGroup = createFurnitureShape(shapeType, newWidth, newHeight, newDepth, material);
    
    // Restore position (adjust Y if needed based on new height)
    newFurnitureGroup.position.copy(currentPosition);
    newFurnitureGroup.rotation.copy(currentRotation);
    
    // Store updated metadata
    newFurnitureGroup.userData = {
        isFurniture: true,
        name: newName,
        originalWidth: newWidth,
        originalHeight: newHeight,
        originalDepth: newDepth,
        color: currentColor,
        volume: newWidth * newHeight * newDepth,
        shapeType: shapeType
    };
    
    // Mark all children as furniture for raycasting
    newFurnitureGroup.traverse(child => {
        if (child.isMesh) {
            child.userData.isFurniture = true;
            child.userData.parentGroup = newFurnitureGroup;
        }
    });
    
    // Add to scene and update array
    scene.add(newFurnitureGroup);
    
    if (index !== -1) {
        furnitureObjects[index] = newFurnitureGroup;
    } else {
        furnitureObjects.push(newFurnitureGroup);
    }
    
    // Constrain to container bounds
    constrainToContainer(newFurnitureGroup);
    
    // Select the new object
    selectObject(newFurnitureGroup);
    
    updateItemsList();
    updateStats();
    checkCollisions();
    
    showToast(`Updated ${newName}`, 'success');
}

function flipSelected() {
    if (!selectedObject) return;
    
    saveState();
    selectedObject.rotation.z += Math.PI / 2;
    
    // Recalculate position to keep object on floor/above floor
    const box = new THREE.Box3().setFromObject(selectedObject);
    const minY = box.min.y;
    
    // Adjust position so object sits on the floor or its current level
    if (minY < 0) {
        selectedObject.position.y -= minY;
    }
    
    constrainToContainer(selectedObject);
    checkCollisions();
    showToast('Flipped', 'success');
}

function moveSelectedUp() {
    if (!selectedObject) return;
    
    const box = new THREE.Box3().setFromObject(selectedObject);
    const size = new THREE.Vector3();
    box.getSize(size);
    
    const newY = selectedObject.position.y + 0.5;
    const maxY = container.height - size.y;
    
    selectedObject.position.y = Math.min(newY, maxY);
    dragPlane.constant = -selectedObject.position.y;
}

function moveSelectedDown() {
    if (!selectedObject) return;
    
    const newY = selectedObject.position.y - 0.5;
    
    selectedObject.position.y = Math.max(newY, 0);
    dragPlane.constant = -selectedObject.position.y;
}

function deleteSelected() {
    if (!selectedObject) return;
    
    saveState();
    
    const name = selectedObject.userData.name;
    scene.remove(selectedObject);
    furnitureObjects = furnitureObjects.filter(obj => obj !== selectedObject);
    selectedObject = null;
    
    document.getElementById('selectedControls').style.display = 'none';
    updateItemsList();
    updateStats();
    updateItemLimitBar();
    checkCollisions();
    
    showToast(`Removed ${name}`, 'success');
}

function constrainToContainer(obj) {
    const box = new THREE.Box3().setFromObject(obj);
    const size = new THREE.Vector3();
    box.getSize(size);
    
    const halfLength = container.length / 2 - size.x / 2;
    const halfWidth = container.width / 2 - size.z / 2;
    const maxHeight = container.height - size.y;
    
    obj.position.x = Math.max(-halfLength, Math.min(halfLength, obj.position.x));
    obj.position.z = Math.max(-halfWidth, Math.min(halfWidth, obj.position.z));
    obj.position.y = Math.max(0, Math.min(maxHeight, obj.position.y));
}

function clearAll() {
    if (furnitureObjects.length === 0) return;
    if (!confirm('Remove all items from the container?')) return;
    
    saveState();
    
    furnitureObjects.forEach(obj => scene.remove(obj));
    furnitureObjects = [];
    selectedObject = null;
    
    document.getElementById('selectedControls').style.display = 'none';
    updateItemsList();
    updateStats();
    updateItemLimitBar();
    checkCollisions();
    
    showToast('All items cleared', 'success');
}

// ============================================
// CONTAINER MANAGEMENT
// ============================================
function selectContainer(id) {
    saveState();
    container = CONTAINER_TEMPLATES.find(c => c.id === id);
    createContainer();
    renderContainerGrid();
    
    // Check if any items don't fit
    furnitureObjects.forEach(obj => constrainToContainer(obj));
    checkCollisions();
    
    showToast(`Switched to ${container.name}`, 'success');
}

// ============================================
// UI UPDATES
// ============================================
function updateItemsList() {
    const list = document.getElementById('itemsList');
    
    if (furnitureObjects.length === 0) {
        list.innerHTML = `
            <div class="empty-state">
                <div class="icon">📦</div>
                <p>No items added yet.<br>Start by adding furniture!</p>
            </div>
        `;
        return;
    }
    
    list.innerHTML = furnitureObjects.map((obj, index) => {
        const data = obj.userData;
        const isSelected = obj === selectedObject;
        return `
            <div class="item-row ${isSelected ? 'selected' : ''}" onclick="selectObjectByIndex(${index})" title="Click to select and edit dimensions">
                <div class="item-info">
                    <div class="item-color" style="background: #${obj.userData.color.toString(16).padStart(6, '0')}"></div>
                    <div>
                        <div class="item-name">${escapeHtml(data.name)}</div>
                        <div class="item-dims">${data.originalWidth}′W × ${data.originalHeight}′H × ${data.originalDepth}′D</div>
                    </div>
                </div>
                <div style="display: flex; align-items: center; gap: 4px;">
                    <span style="font-size: 12px; color: var(--text-muted);" title="Click to edit">✏️</span>
                    <button class="item-delete" onclick="event.stopPropagation(); deleteObjectByIndex(${index})">×</button>
                </div>
            </div>
        `;
    }).join('');
}

function selectObjectByIndex(index) {
    selectObject(furnitureObjects[index]);
}

function deleteObjectByIndex(index) {
    const obj = furnitureObjects[index];
    if (obj === selectedObject) {
        selectedObject = null;
        document.getElementById('selectedControls').style.display = 'none';
    }
    scene.remove(obj);
    furnitureObjects.splice(index, 1);
    updateItemsList();
    updateStats();
}

function updateStats() {
    const containerVolume = container.length * container.width * container.height;
    const usedVolume = furnitureObjects.reduce((sum, obj) => sum + obj.userData.volume, 0);
    const percentage = (usedVolume / containerVolume) * 100;

    document.getElementById('spaceUsed').textContent = `${usedVolume.toFixed(0)} / ${containerVolume.toLocaleString()} ft³`;
    document.getElementById('itemCount').textContent = furnitureObjects.length;

    const progressFill = document.getElementById('progressFill');
    progressFill.style.width = `${Math.min(100, percentage)}%`;

    // Color-code progress bar based on fill percentage
    progressFill.className = 'progress-fill';
    if (percentage >= 90) {
        progressFill.classList.add('danger');
    } else if (percentage >= 70) {
        progressFill.classList.add('warning');
    }
}

function showToast(message, type = 'success') {
    const toast = document.getElementById('toast');
    toast.textContent = message;
    toast.className = `toast ${type} show`;
    
    setTimeout(() => {
        toast.classList.remove('show');
    }, 2000);
}

// ============================================
// UNDO SYSTEM
// ============================================
function saveState() {
    const state = {
        containerId: container.id,
        furniture: furnitureObjects.map(obj => ({
            name: obj.userData.name,
            width: obj.userData.originalWidth,
            height: obj.userData.originalHeight,
            depth: obj.userData.originalDepth,
            color: obj.userData.color,
            shapeType: obj.userData.shapeType,
            position: { x: obj.position.x, y: obj.position.y, z: obj.position.z },
            rotation: { x: obj.rotation.x, y: obj.rotation.y, z: obj.rotation.z }
        }))
    };
    
    undoHistory.push(JSON.stringify(state));
    if (undoHistory.length > MAX_UNDO_HISTORY) {
        undoHistory.shift();
    }
    
    document.getElementById('undoBtn').disabled = undoHistory.length <= 1;
}

function undoAction() {
    if (undoHistory.length <= 1) return;
    
    // Check free tier undo limit
    if (!isPro) {
        const undosUsed = parseInt(sessionStorage.getItem('undosUsed') || '0');
        if (undosUsed >= FREE_LIMITS.maxUndos) {
            showPaywall('Undo Limit Reached', `You've used all ${FREE_LIMITS.maxUndos} free undos. Upgrade to Pro for unlimited undos!`);
            return;
        }
        trackUndo();
    }
    
    // Remove current state
    undoHistory.pop();
    
    // Get previous state
    const prevState = JSON.parse(undoHistory[undoHistory.length - 1]);
    
    // Restore state
    restoreState(prevState);
    
    document.getElementById('undoBtn').disabled = undoHistory.length <= 1;
    showToast('Undone', 'success');
}

function restoreState(state) {
    // Clear current furniture
    furnitureObjects.forEach(obj => scene.remove(obj));
    furnitureObjects = [];
    selectedObject = null;
    document.getElementById('selectedControls').style.display = 'none';
    
    // Restore container
    if (state.containerId !== container.id) {
        container = CONTAINER_TEMPLATES.find(c => c.id === state.containerId);
        createContainer();
        renderContainerGrid();
    }
    
    // Restore furniture
    state.furniture.forEach(item => {
        const material = new THREE.MeshLambertMaterial({ color: item.color });
        const group = createFurnitureShape(item.shapeType, item.width, item.height, item.depth, material);
        
        group.position.set(item.position.x, item.position.y, item.position.z);
        group.rotation.set(item.rotation.x, item.rotation.y, item.rotation.z);
        
        group.userData = {
            isFurniture: true,
            name: item.name,
            originalWidth: item.width,
            originalHeight: item.height,
            originalDepth: item.depth,
            color: item.color,
            volume: item.width * item.height * item.depth,
            shapeType: item.shapeType
        };
        
        group.traverse(child => {
            if (child.isMesh) {
                child.userData.isFurniture = true;
                child.userData.parentGroup = group;
            }
        });
        
        scene.add(group);
        furnitureObjects.push(group);
    });
    
    updateItemsList();
    updateStats();
    updateItemLimitBar();
    checkCollisions();
}

// ============================================
// DUPLICATE
// ============================================
function duplicateSelected() {
    if (!selectedObject) return;
    
    // Pro-only feature
    if (!isPro) {
        showPaywall('Pro Feature', 'Duplicate items is a Pro feature. Upgrade to quickly copy furniture pieces.');
        return;
    }
    
    saveState();
    
    const data = selectedObject.userData;
    const material = new THREE.MeshLambertMaterial({ color: COLORS[furnitureObjects.length % COLORS.length] });
    const group = createFurnitureShape(data.shapeType, data.originalWidth, data.originalHeight, data.originalDepth, material);
    
    // Position slightly offset from original
    group.position.copy(selectedObject.position);
    group.position.x += 1;
    group.position.z += 1;
    group.rotation.copy(selectedObject.rotation);
    
    group.userData = {
        isFurniture: true,
        name: data.name + ' (copy)',
        originalWidth: data.originalWidth,
        originalHeight: data.originalHeight,
        originalDepth: data.originalDepth,
        color: COLORS[furnitureObjects.length % COLORS.length],
        volume: data.volume,
        shapeType: data.shapeType
    };
    
    group.traverse(child => {
        if (child.isMesh) {
            child.userData.isFurniture = true;
            child.userData.parentGroup = group;
        }
    });
    
    constrainToContainer(group);
    
    scene.add(group);
    furnitureObjects.push(group);
    
    selectObject(group);
    updateItemsList();
    updateStats();
    checkCollisions();
    
    showToast('Item duplicated', 'success');
}

// ============================================
// COLLISION DETECTION
// ============================================
function checkCollisions() {
    // Free users don't get collision detection
    if (!isPro) {
        document.getElementById('collisionWarning').style.display = 'none';
        return new Set();
    }
    
    const collisions = new Set();
    
    for (let i = 0; i < furnitureObjects.length; i++) {
        const box1 = new THREE.Box3().setFromObject(furnitureObjects[i]);
        // Shrink box slightly to avoid false positives from touching items
        box1.min.addScalar(0.05);
        box1.max.subScalar(0.05);
        
        for (let j = i + 1; j < furnitureObjects.length; j++) {
            const box2 = new THREE.Box3().setFromObject(furnitureObjects[j]);
            box2.min.addScalar(0.05);
            box2.max.subScalar(0.05);
            
            if (box1.intersectsBox(box2)) {
                collisions.add(i);
                collisions.add(j);
            }
        }
    }
    
    // Update visual feedback
    furnitureObjects.forEach((obj, index) => {
        const hasCollision = collisions.has(index);
        obj.traverse(child => {
            if (child.isMesh && child.material) {
                if (hasCollision) {
                    child.material.emissive = new THREE.Color(0x992222);
                } else if (obj !== selectedObject) {
                    child.material.emissive = new THREE.Color(0x000000);
                }
            }
        });
    });
    
    // Update warning display
    const warning = document.getElementById('collisionWarning');
    if (collisions.size > 0) {
        warning.style.display = 'inline';
        warning.textContent = `⚠️ ${collisions.size} items overlapping!`;
    } else {
        warning.style.display = 'none';
    }
    
    // Update items list to show collision state
    updateItemsListCollisions(collisions);
    
    return collisions;
}

function updateItemsListCollisions(collisions) {
    const itemRows = document.querySelectorAll('.item-row');
    itemRows.forEach((row, index) => {
        if (collisions.has(index)) {
            row.classList.add('collision');
        } else {
            row.classList.remove('collision');
        }
    });
}

// ============================================
// SAVE / LOAD
// ============================================
function saveProject() {
    selectedSaveSlot = 0;
    renderSaveSlots();
    showModal('saveModal');
}

function loadProject() {
    renderLoadSlots();
    showModal('loadModal');
}

// Cloud saves cache (for logged-in users)
let cloudSavesCache = [];

async function renderSaveSlots() {
    const containerEl = document.getElementById('saveSlots');
    const maxSlots = isPro ? 3 : FREE_LIMITS.maxSaveSlots;
    let html = '';

    // Check if user is logged in and Supabase is configured
    const useCloudSaves = isUserLoggedIn() && isSupabaseConfigured();

    // Fetch cloud saves if logged in
    if (useCloudSaves) {
        try {
            cloudSavesCache = await getSavedProjects();
        } catch (e) {
            console.error('Failed to fetch cloud saves:', e);
            cloudSavesCache = [];
        }
    }

    for (let i = 0; i < 3; i++) {
        let slotData = null;

        if (useCloudSaves) {
            // Look for cloud save in this slot
            const cloudSave = cloudSavesCache.find(s => s.slot === i);
            if (cloudSave) {
                slotData = {
                    name: cloudSave.name,
                    savedAt: cloudSave.updated_at,
                    itemCount: cloudSave.item_count,
                    containerName: cloudSave.container_name,
                    isCloud: true,
                };
            }
        } else {
            // Use localStorage
            const saved = localStorage.getItem(`willitfit_save_${i}`);
            if (saved) {
                try {
                    slotData = JSON.parse(saved);
                } catch (e) {}
            }
        }

        const isSelected = selectedSaveSlot === i;
        const isEmpty = !slotData;
        const isLocked = !isPro && i >= FREE_LIMITS.maxSaveSlots;

        if (isLocked) {
            html += `
                <div class="save-slot empty locked" onclick="showPaywall('Pro Feature', 'Additional save slots are a Pro feature. Upgrade to save up to 3 packing plans.')">
                    <div class="save-slot-info">
                        <div class="save-slot-name">🔒 Slot ${i + 1} (Pro)</div>
                        <div class="save-slot-meta">Upgrade to unlock</div>
                    </div>
                </div>
            `;
        } else {
            const cloudBadge = slotData?.isCloud ? '<span style="font-size: 10px; color: var(--accent);">☁️</span> ' : '';
            html += `
                <div class="save-slot ${isEmpty ? 'empty' : ''} ${isSelected ? 'selected' : ''}"
                     onclick="selectedSaveSlot = ${i}; renderSaveSlots();"
                     style="${isSelected ? 'border: 2px solid var(--primary);' : ''}">
                    <div class="save-slot-info">
                        <div class="save-slot-name">${cloudBadge}${slotData ? escapeHtml(slotData.name) : 'Empty Slot ' + (i + 1)}</div>
                        <div class="save-slot-meta">
                            ${slotData ? `${escapeHtml(slotData.itemCount)} items · ${escapeHtml(slotData.containerName)} · ${new Date(slotData.savedAt).toLocaleDateString()}` : 'No save data'}
                        </div>
                    </div>
                    ${slotData ? `<button class="save-slot-btn delete" onclick="event.stopPropagation(); deleteSaveSlot(${i})">🗑️</button>` : ''}
                </div>
            `;
        }
    }

    containerEl.innerHTML = html;
}

async function renderLoadSlots() {
    const containerEl = document.getElementById('loadSlots');
    let html = '';
    let hasAnySave = false;

    // Check if user is logged in and Supabase is configured
    const useCloudSaves = isUserLoggedIn() && isSupabaseConfigured();

    // Fetch cloud saves if logged in
    if (useCloudSaves) {
        try {
            cloudSavesCache = await getSavedProjects();
        } catch (e) {
            console.error('Failed to fetch cloud saves:', e);
            cloudSavesCache = [];
        }
    }

    for (let i = 0; i < 3; i++) {
        let slotData = null;

        if (useCloudSaves) {
            const cloudSave = cloudSavesCache.find(s => s.slot === i);
            if (cloudSave) {
                slotData = {
                    name: cloudSave.name,
                    savedAt: cloudSave.updated_at,
                    itemCount: cloudSave.item_count,
                    containerName: cloudSave.container_name,
                    isCloud: true,
                };
                hasAnySave = true;
            }
        } else {
            const saved = localStorage.getItem(`willitfit_save_${i}`);
            if (saved) {
                try {
                    slotData = JSON.parse(saved);
                    hasAnySave = true;
                } catch (e) {}
            }
        }

        if (slotData) {
            const cloudBadge = slotData.isCloud ? '<span style="font-size: 10px; color: var(--accent);">☁️</span> ' : '';
            html += `
                <div class="save-slot" onclick="loadFromSlot(${i})">
                    <div class="save-slot-info">
                        <div class="save-slot-name">${cloudBadge}${escapeHtml(slotData.name)}</div>
                        <div class="save-slot-meta">
                            ${escapeHtml(slotData.itemCount)} items · ${escapeHtml(slotData.containerName)} · ${new Date(slotData.savedAt).toLocaleDateString()}
                        </div>
                    </div>
                    <button class="save-slot-btn" onclick="event.stopPropagation(); loadFromSlot(${i})">Load</button>
                </div>
            `;
        }
    }

    if (!hasAnySave) {
        html = '<div class="empty-state"><p>No saved projects yet.</p></div>';
    }

    containerEl.innerHTML = html;
}

async function saveToSlot() {
    const projectName = document.getElementById('saveProjectName').value || `Project ${selectedSaveSlot + 1}`;

    const state = {
        name: projectName,
        savedAt: new Date().toISOString(),
        itemCount: furnitureObjects.length,
        containerName: container.name,
        containerId: container.id,
        furniture: furnitureObjects.map(obj => ({
            name: obj.userData.name,
            width: obj.userData.originalWidth,
            height: obj.userData.originalHeight,
            depth: obj.userData.originalDepth,
            color: obj.userData.color,
            shapeType: obj.userData.shapeType,
            position: { x: obj.position.x, y: obj.position.y, z: obj.position.z },
            rotation: { x: obj.rotation.x, y: obj.rotation.y, z: obj.rotation.z }
        }))
    };

    // Check if user is logged in and Supabase is configured
    const useCloudSaves = isUserLoggedIn() && isSupabaseConfigured();

    if (useCloudSaves) {
        try {
            await saveProjectToCloud(selectedSaveSlot, state);
            closeModal('saveModal');
            showToast(`Saved to cloud: "${projectName}"`, 'success');
        } catch (error) {
            console.error('Failed to save to cloud:', error);
            // Fall back to localStorage
            localStorage.setItem(`willitfit_save_${selectedSaveSlot}`, JSON.stringify(state));
            closeModal('saveModal');
            showToast(`Saved locally: "${projectName}"`, 'success');
        }
    } else {
        // Save to localStorage
        localStorage.setItem(`willitfit_save_${selectedSaveSlot}`, JSON.stringify(state));
        closeModal('saveModal');
        showToast(`Saved to "${projectName}"`, 'success');

        // Maybe show account prompt for second save slot
        if (selectedSaveSlot > 0) {
            setTimeout(() => maybeShowAccountPrompt('save'), 500);
        }
    }
}

async function loadFromSlot(slot) {
    // Check if user is logged in and Supabase is configured
    const useCloudSaves = isUserLoggedIn() && isSupabaseConfigured();

    try {
        let state;

        if (useCloudSaves) {
            // Load from cloud
            const cloudSave = await loadProjectFromCloud(slot);
            if (!cloudSave) {
                showToast('Save not found', 'error');
                return;
            }
            state = cloudSave.data;
        } else {
            // Load from localStorage
            const saved = localStorage.getItem(`willitfit_save_${slot}`);
            if (!saved) {
                showToast('Save not found', 'error');
                return;
            }
            state = JSON.parse(saved);
        }

        // Clear undo history and save initial state
        undoHistory = [];

        // Restore the state
        restoreState(state);

        // Save as initial state for undo
        saveState();

        closeModal('loadModal');
        showToast(`Loaded "${state.name}"`, 'success');
    } catch (e) {
        console.error('Failed to load save:', e);
        showToast('Failed to load save', 'error');
    }
}

async function deleteSaveSlot(slot) {
    if (!confirm('Delete this saved project?')) return;

    // Check if user is logged in and Supabase is configured
    const useCloudSaves = isUserLoggedIn() && isSupabaseConfigured();

    try {
        if (useCloudSaves) {
            await deleteProjectFromCloud(slot);
        } else {
            localStorage.removeItem(`willitfit_save_${slot}`);
        }

        renderSaveSlots();
        showToast('Save deleted', 'success');
    } catch (error) {
        console.error('Failed to delete save:', error);
        showToast('Failed to delete save', 'error');
    }
}

// ============================================
// EXPORT
// ============================================
function exportProject() {
    const totalVolume = container.length * container.width * container.height;
    const usedVolume = furnitureObjects.reduce((sum, obj) => sum + obj.userData.volume, 0);
    const efficiency = ((usedVolume / totalVolume) * 100).toFixed(1);
    
    let summary = `═══════════════════════════════════════
   WILL IT FIT? - PACKING PLAN
═══════════════════════════════════════
${!isPro ? '\n⚠️ FREE VERSION - Upgrade to Pro to remove watermark\n' : ''}
📦 CONTAINER: ${container.name}
   Dimensions: ${container.length}' L × ${container.width}' W × ${container.height}' H
   Total Capacity: ${totalVolume.toLocaleString()} ft³

📊 SPACE USAGE:
   Used: ${usedVolume.toFixed(1)} ft³
   Remaining: ${(totalVolume - usedVolume).toFixed(1)} ft³
   Efficiency: ${efficiency}%

───────────────────────────────────────
📋 ITEMS (${furnitureObjects.length} total)
───────────────────────────────────────
`;
    
    furnitureObjects.forEach((obj, i) => {
        const d = obj.userData;
        summary += `
${i + 1}. ${d.name}
   Size: ${d.originalWidth}'W × ${d.originalHeight}'H × ${d.originalDepth}'D
   Volume: ${d.volume.toFixed(1)} ft³`;
    });
    
    summary += `

───────────────────────────────────────
Generated by Will It Fit? Simulator
Date: ${new Date().toLocaleString()}
${!isPro ? '🔓 Upgrade to Pro at willitfit.app/pro' : ''}
═══════════════════════════════════════`;
    
    document.getElementById('exportSummary').textContent = summary;
    showModal('exportModal');
}

function copyExportSummary() {
    const summary = document.getElementById('exportSummary').textContent;
    navigator.clipboard.writeText(summary).then(() => {
        showToast('Copied to clipboard!', 'success');
    }).catch(() => {
        showToast('Failed to copy', 'error');
    });
}

// ============================================
// MODALS
// ============================================
function showModal(id) {
    document.getElementById(id).classList.add('show');
}

function closeModal(id) {
    document.getElementById(id).classList.remove('show');
}

function showOnboarding() {
    if (!localStorage.getItem('willitfit_onboarded')) {
        showModal('onboardingModal');
    }
}

// ============================================
// FREEMIUM SYSTEM
// ============================================
function updateItemLimitBar() {
    const limitBar = document.getElementById('itemLimitBar');
    const limitCount = document.getElementById('limitCount');
    const limitFill = document.getElementById('limitProgressFill');
    const upgradeBtn = document.getElementById('upgradeBtn');
    
    if (isPro) {
        limitBar.classList.add('hidden');
        upgradeBtn.classList.add('hidden');
        return;
    }
    
    limitBar.classList.remove('hidden');
    upgradeBtn.classList.remove('hidden');
    
    const count = furnitureObjects.length;
    const max = FREE_LIMITS.maxItems;
    const percent = (count / max) * 100;
    
    limitCount.textContent = `${count}/${max} free`;
    limitFill.style.width = `${Math.min(100, percent)}%`;
    
    // Color coding
    limitCount.className = 'limit-count';
    limitFill.className = 'limit-progress-fill';
    
    if (count >= max) {
        limitCount.classList.add('danger');
        limitFill.classList.add('danger');
    } else if (count >= max - 1) {
        limitCount.classList.add('warning');
        limitFill.classList.add('warning');
    }
}

function canAddItem() {
    if (isPro) return true;
    return furnitureObjects.length < FREE_LIMITS.maxItems;
}

function canUseSaveSlot(slot) {
    if (isPro) return true;
    return slot < FREE_LIMITS.maxSaveSlots;
}

function canUndo() {
    if (isPro) return undoHistory.length > 1;
    // Free users: track undo count per session
    const undosUsed = parseInt(sessionStorage.getItem('undosUsed') || '0');
    return undoHistory.length > 1 && undosUsed < FREE_LIMITS.maxUndos;
}

function trackUndo() {
    if (isPro) return;
    const undosUsed = parseInt(sessionStorage.getItem('undosUsed') || '0');
    sessionStorage.setItem('undosUsed', (undosUsed + 1).toString());
}

function showPaywall(title, description) {
    document.getElementById('paywallTitle').textContent = title;
    document.getElementById('paywallDesc').textContent = description;
    document.getElementById('paywallOverlay').classList.add('show');
}

function hidePaywallToast() {
    document.getElementById('paywallOverlay').classList.remove('show');
}

// ============================================
// MOBILE UI TOGGLES
// ============================================

function toggleSearch() {
    const container = document.getElementById('searchContainer');
    const toggle = document.getElementById('searchToggle');
    const input = document.getElementById('furnitureSearch');

    if (container.style.display === 'none') {
        container.style.display = 'flex';
        toggle.classList.add('active');
        input.focus();
    } else {
        container.style.display = 'none';
        toggle.classList.remove('active');
        input.value = '';
        filterFurniture(); // Reset filter
    }
}

function toggleCustomItem() {
    const form = document.getElementById('customItemForm');
    const icon = document.getElementById('customItemIcon');

    if (form.style.display === 'none') {
        form.style.display = 'block';
        icon.classList.add('expanded');
    } else {
        form.style.display = 'none';
        icon.classList.remove('expanded');
    }
}

function renderUpgradeFeatures() {
    const container = document.getElementById('upgradeFeatures');
    container.innerHTML = PRO_FEATURES.map(f => `
        <div class="upgrade-feature">
            <div class="feature-icon">${f.icon}</div>
            <div class="feature-content">
                <div class="feature-title">${f.title}</div>
                <div class="feature-desc">${f.desc}</div>
            </div>
        </div>
    `).join('');
}

function purchasePro() {
    // Open the payment form modal
    closeModal('upgradeModal');
    showModal('paymentModal');
}

// ============================================
// PAYMENT FORM HANDLING
// ============================================
function formatCardNumber(input) {
    let value = input.value.replace(/\D/g, '');
    value = value.substring(0, 16);
    
    // Add spaces every 4 digits
    let formatted = '';
    for (let i = 0; i < value.length; i++) {
        if (i > 0 && i % 4 === 0) formatted += ' ';
        formatted += value[i];
    }
    input.value = formatted;
    
    // Detect card brand
    detectCardBrand(value);
}

function detectCardBrand(number) {
    const visa = document.getElementById('brandVisa');
    const mc = document.getElementById('brandMC');
    const amex = document.getElementById('brandAmex');
    
    // Reset all
    visa.classList.remove('active');
    mc.classList.remove('active');
    amex.classList.remove('active');
    
    if (number.startsWith('4')) {
        visa.classList.add('active');
    } else if (/^5[1-5]/.test(number) || /^2[2-7]/.test(number)) {
        mc.classList.add('active');
    } else if (/^3[47]/.test(number)) {
        amex.classList.add('active');
    }
}

function formatExpiry(input) {
    let value = input.value.replace(/\D/g, '');
    value = value.substring(0, 4);
    
    if (value.length >= 2) {
        value = value.substring(0, 2) + ' / ' + value.substring(2);
    }
    input.value = value;
}

function formatCvc(input) {
    input.value = input.value.replace(/\D/g, '').substring(0, 4);
}

function validateCard() {
    const cardNumber = document.getElementById('cardNumber').value.replace(/\s/g, '');
    const expiry = document.getElementById('cardExpiry').value.replace(/\s/g, '');
    const cvc = document.getElementById('cardCvc').value;
    const name = document.getElementById('cardName').value;
    const email = document.getElementById('paymentEmail').value;
    const country = document.getElementById('billingCountry').value;
    const zip = document.getElementById('billingZip').value;
    
    // Basic validation
    if (!email || !email.includes('@')) {
        return { valid: false, error: 'Please enter a valid email address.' };
    }
    
    if (cardNumber.length < 15) {
        return { valid: false, error: 'Please enter a valid card number.' };
    }
    
    // Luhn algorithm check
    if (!luhnCheck(cardNumber)) {
        return { valid: false, error: 'Invalid card number. Please check and try again.' };
    }
    
    if (expiry.length < 4) {
        return { valid: false, error: 'Please enter a valid expiration date.' };
    }
    
    // Check expiry is in future
    const [month, year] = expiry.split('/');
    const expMonth = parseInt(month);
    const expYear = parseInt('20' + year);
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;
    
    if (expMonth < 1 || expMonth > 12) {
        return { valid: false, error: 'Invalid expiration month.' };
    }
    
    if (expYear < currentYear || (expYear === currentYear && expMonth < currentMonth)) {
        return { valid: false, error: 'Card has expired. Please use a valid card.' };
    }
    
    if (cvc.length < 3) {
        return { valid: false, error: 'Please enter a valid CVC code.' };
    }
    
    if (!name.trim()) {
        return { valid: false, error: 'Please enter the name on your card.' };
    }
    
    if (!country) {
        return { valid: false, error: 'Please select your country.' };
    }
    
    if (!zip.trim()) {
        return { valid: false, error: 'Please enter your ZIP/postal code.' };
    }
    
    return { 
        valid: true, 
        data: {
            email,
            cardNumber,
            expMonth,
            expYear,
            cvc,
            name,
            country,
            zip
        }
    };
}

// Luhn algorithm for card validation
function luhnCheck(cardNumber) {
    let sum = 0;
    let isEven = false;
    
    for (let i = cardNumber.length - 1; i >= 0; i--) {
        let digit = parseInt(cardNumber[i]);
        
        if (isEven) {
            digit *= 2;
            if (digit > 9) digit -= 9;
        }
        
        sum += digit;
        isEven = !isEven;
    }
    
    return sum % 10 === 0;
}

async function processPayment(event) {
    event.preventDefault();

    const errorDiv = document.getElementById('paymentError');
    const payButton = document.getElementById('payButton');
    const payButtonText = document.getElementById('payButtonText');
    const payButtonSpinner = document.getElementById('payButtonSpinner');

    // Get selected plan
    const selectedPlan = window.getSelectedPlan ? window.getSelectedPlan() : { priceDisplay: '$9.99' };

    // Hide previous errors
    errorDiv.style.display = 'none';

    // In production, Stripe Elements owns the card fields — we only
    // validate what we collect ourselves. The demo form validates
    // everything via validateCardForm.
    let paymentData;
    if (isProductionMode()) {
        const email = document.getElementById('paymentEmail')?.value.trim() || '';
        const name = document.getElementById('cardName')?.value.trim() || '';
        if (!email || !email.includes('@')) {
            errorDiv.textContent = 'Please enter a valid email address.';
            errorDiv.style.display = 'block';
            return;
        }
        if (!name) {
            errorDiv.textContent = 'Please enter the name on your card.';
            errorDiv.style.display = 'block';
            return;
        }
        paymentData = { email, name };
    } else {
        const validation = validateCardForm();
        if (!validation.valid) {
            errorDiv.textContent = validation.error;
            errorDiv.style.display = 'block';
            return;
        }
        paymentData = validation.data;
    }

    // Show loading state
    payButton.disabled = true;
    payButtonText.textContent = 'Processing...';
    payButtonSpinner.style.display = 'block';

    try {
        // Process payment using the payment module
        // This handles both Stripe (production) and demo mode automatically
        const result = await processStripePayment(paymentData.email, paymentData);

        // Success! Pass plan details to unlockPro
        unlockPro({
            planType: result.planType,
            planName: result.planName,
            expiresAt: result.expiresAt,
        });

        // Store the email for receipts
        localStorage.setItem('willitfit_email', paymentData.email);

    } catch (error) {
        errorDiv.textContent = error.message || 'Payment failed. Please try again.';
        errorDiv.style.display = 'block';
    } finally {
        // Reset button state
        payButton.disabled = false;
        payButtonText.textContent = `Pay ${selectedPlan.priceDisplay}`;
        payButtonSpinner.style.display = 'none';
    }
}

function unlockPro(planDetails = null) {
    isPro = true;
    localStorage.setItem('willitfit_pro', 'true');

    // Store plan details if provided
    if (planDetails) {
        const proData = {
            planType: planDetails.planType,
            planName: planDetails.planName,
            purchaseDate: new Date().toISOString(),
            expiresAt: planDetails.expiresAt,
        };
        localStorage.setItem('willitfit_pro_data', JSON.stringify(proData));
    }

    closeModal('upgradeModal');
    closeModal('paymentModal');
    hidePaywallToast();

    // Refresh UI
    updateItemLimitBar();
    renderFurnitureGrid();
    renderContainerGrid();
    renderSaveSlots();
    updateUpgradeButton();

    // Show appropriate message
    if (planDetails?.planName) {
        const daysMsg = planDetails.planType === 'pro_annual' ? '365 days' : '60 days';
        showToast(`🎉 ${planDetails.planName} activated! Enjoy ${daysMsg} of unlimited access!`, 'success');
    } else {
        showToast('🎉 Pro unlocked! Enjoy all features!', 'success');
    }
}

// Update the upgrade button based on Pro status
function updateUpgradeButton() {
    const upgradeBtn = document.getElementById('upgradeBtn');
    if (!upgradeBtn) return;

    if (isPro) {
        const details = getProPlanDetails();
        if (details?.daysRemaining !== null && details.daysRemaining <= 7) {
            // Plan expiring soon - show warning
            upgradeBtn.innerHTML = `⚠️ ${details.daysRemaining}d left`;
            upgradeBtn.classList.add('expiring');
        } else {
            // Active Pro - show status
            upgradeBtn.innerHTML = '✓ Pro';
            upgradeBtn.classList.add('active');
        }
    } else {
        upgradeBtn.innerHTML = '✨ Pro';
        upgradeBtn.classList.remove('active', 'expiring');
    }
}

// Email pending verification in the restore flow
let restorePendingEmail = null;

function restorePurchase() {
    // Close upgrade modal and show restore modal
    closeModal('upgradeModal');
    showModal('restoreModal');

    // Reset to step 1 and clear previous state
    restoreUseDifferentEmail();
    const emailInput = document.getElementById('restoreEmail');
    if (emailInput) emailInput.value = '';
}

function restoreUseDifferentEmail() {
    restorePendingEmail = null;

    const stepEmail = document.getElementById('restoreStepEmail');
    const stepCode = document.getElementById('restoreStepCode');
    if (stepEmail) stepEmail.style.display = 'block';
    if (stepCode) stepCode.style.display = 'none';

    const errorEl = document.getElementById('restoreError');
    const codeErrorEl = document.getElementById('restoreCodeError');
    const codeInput = document.getElementById('restoreCode');
    if (errorEl) errorEl.style.display = 'none';
    if (codeErrorEl) codeErrorEl.style.display = 'none';
    if (codeInput) codeInput.value = '';
}

// Step 1: send a verification code to the email the user claims to own
async function handleRestoreEmailSubmit(event) {
    event.preventDefault();

    const emailInput = document.getElementById('restoreEmail');
    const errorEl = document.getElementById('restoreError');
    const button = document.getElementById('restoreButton');
    const buttonText = document.getElementById('restoreButtonText');
    const spinner = document.getElementById('restoreSpinner');

    const email = emailInput.value.trim();

    errorEl.style.display = 'none';
    button.disabled = true;
    buttonText.textContent = 'Sending...';
    spinner.style.display = 'inline-block';

    try {
        if (isSupabaseConfigured()) {
            await requestRestoreCode(email);
            restorePendingEmail = email;

            // Advance to the code step
            document.getElementById('restoreCodeEmail').textContent = email;
            document.getElementById('restoreStepEmail').style.display = 'none';
            document.getElementById('restoreStepCode').style.display = 'block';
            document.getElementById('restoreCode').focus();
        } else {
            // Demo mode - no backend to send codes; simulate with demo email
            if (email.toLowerCase() === 'demo@test.com') {
                closeModal('restoreModal');
                unlockPro();
            } else {
                throw new Error('Demo mode: use demo@test.com to simulate a restore.');
            }
        }
    } catch (error) {
        errorEl.textContent = error.message || 'Could not send a verification code. Please try again.';
        errorEl.style.display = 'block';
    } finally {
        button.disabled = false;
        buttonText.textContent = 'Email Me a Code';
        spinner.style.display = 'none';
    }
}

// Step 2: verify the emailed code, then unlock if a purchase exists
async function handleRestoreCodeSubmit(event) {
    event.preventDefault();

    const codeInput = document.getElementById('restoreCode');
    const errorEl = document.getElementById('restoreCodeError');
    const button = document.getElementById('restoreCodeButton');
    const buttonText = document.getElementById('restoreCodeButtonText');
    const spinner = document.getElementById('restoreCodeSpinner');

    errorEl.style.display = 'none';
    button.disabled = true;
    buttonText.textContent = 'Verifying...';
    spinner.style.display = 'inline-block';

    try {
        if (!restorePendingEmail) {
            throw new Error('Please request a verification code first.');
        }

        const result = await confirmRestoreCode(restorePendingEmail, codeInput.value);
        if (result.success) {
            closeModal('restoreModal');
            unlockPro();
            localStorage.setItem('willitfit_email', restorePendingEmail);
            restorePendingEmail = null;
        }
    } catch (error) {
        errorEl.textContent = error.message || 'Verification failed. Please try again.';
        errorEl.style.display = 'block';
    } finally {
        button.disabled = false;
        buttonText.textContent = 'Verify & Restore';
        spinner.style.display = 'none';
    }
}

function updateProUI() {
    // Re-check Pro status (handles expiration)
    isPro = checkProStatus();

    updateItemLimitBar();
    renderUpgradeFeatures();
    updateUpgradeButton();

    // Update duplicate button visibility
    const duplicateBtn = document.querySelector('[onclick="duplicateSelected()"]');
    if (duplicateBtn && !isPro) {
        duplicateBtn.onclick = () => showPaywall('Pro Feature', 'Duplicate items is a Pro feature. Upgrade to quickly copy furniture pieces.');
    }
}

// ============================================
// TOUCH & VIEWPORT HINTS
// ============================================
function showTouchHints() {
    // Only show on mobile and if not dismissed before
    if (window.innerWidth > 900 || localStorage.getItem('willitfit_touch_hints_dismissed')) {
        return;
    }
    const touchHints = document.getElementById('touchHints');
    if (touchHints) {
        touchHints.classList.add('show');
    }
}

function dismissTouchHints() {
    const touchHints = document.getElementById('touchHints');
    if (touchHints) {
        touchHints.classList.remove('show');
    }
    localStorage.setItem('willitfit_touch_hints_dismissed', 'true');
}

function showViewportHint() {
    // Only show if not dismissed before
    if (localStorage.getItem('willitfit_viewport_hint_dismissed')) {
        return;
    }
    const viewportHint = document.getElementById('viewportHint');
    if (viewportHint) {
        viewportHint.classList.add('show');
        // Auto-dismiss after 5 seconds
        setTimeout(() => {
            viewportHint.classList.remove('show');
            localStorage.setItem('willitfit_viewport_hint_dismissed', 'true');
        }, 5000);
    }
}

// Show hints when user first adds an item
function showFirstItemHints() {
    if (furnitureObjects.length === 1) {
        // First item added - show viewport hint
        showViewportHint();
        // Show touch hints on mobile
        if (window.innerWidth <= 900) {
            setTimeout(showTouchHints, 1000);
        }
    }
}

// ============================================
// AUTO-PACK
// ============================================
function autoPack() {
    if (furnitureObjects.length === 0) {
        showToast('No items to arrange', 'error');
        return;
    }

    saveState();

    // Sort items largest volume first
    const sorted = [...furnitureObjects].sort((a, b) => b.userData.volume - a.userData.volume);

    // Reset all rotations to 0 for predictable packing
    sorted.forEach(obj => {
        obj.rotation.set(0, 0, 0);
    });

    // Simple left-to-right, front-to-back, bottom-up packing
    const padding = 0.1;
    let cursorX = -container.length / 2 + padding;
    let cursorZ = -container.width / 2 + padding;
    let cursorY = 0;
    let rowMaxDepth = 0;
    let layerMaxHeight = 0;

    sorted.forEach(obj => {
        const w = obj.userData.originalWidth;
        const h = obj.userData.originalHeight;
        const d = obj.userData.originalDepth;

        // Check if item fits in current row
        if (cursorX + w > container.length / 2 - padding) {
            // Move to next row
            cursorX = -container.length / 2 + padding;
            cursorZ += rowMaxDepth + padding;
            rowMaxDepth = 0;
        }

        // Check if item fits in current layer
        if (cursorZ + d > container.width / 2 - padding) {
            // Move to next layer (stack up)
            cursorX = -container.length / 2 + padding;
            cursorZ = -container.width / 2 + padding;
            cursorY += layerMaxHeight + padding;
            rowMaxDepth = 0;
            layerMaxHeight = 0;
        }

        // Place item
        obj.position.x = cursorX + w / 2;
        obj.position.z = cursorZ + d / 2;
        obj.position.y = cursorY;

        // Advance cursor
        cursorX += w + padding;
        rowMaxDepth = Math.max(rowMaxDepth, d);
        layerMaxHeight = Math.max(layerMaxHeight, h);
    });

    // Constrain all items
    sorted.forEach(obj => constrainToContainer(obj));

    updateItemsList();
    checkCollisions();
    showToast('Items auto-packed!', 'success');
}

// ============================================
// DIMENSION LABELS
// ============================================
let dimensionLabels = [];

function createDimensionLabels(obj) {
    removeDimensionLabels();

    const data = obj.userData;
    const w = data.originalWidth;
    const h = data.originalHeight;
    const d = data.originalDepth;
    const box = new THREE.Box3().setFromObject(obj);
    const center = new THREE.Vector3();
    box.getCenter(center);
    const min = box.min;
    const max = box.max;

    const labels = [
        { text: `${w}' W`, pos: new THREE.Vector3(center.x, min.y - 0.3, max.z + 0.3) },
        { text: `${h}' H`, pos: new THREE.Vector3(max.x + 0.3, center.y, center.z) },
        { text: `${d}' D`, pos: new THREE.Vector3(min.x - 0.3, min.y - 0.3, center.z) },
    ];

    labels.forEach(({ text, pos }) => {
        const canvas = document.createElement('canvas');
        canvas.width = 128;
        canvas.height = 48;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = 'rgba(0, 0, 0, 0.75)';
        ctx.roundRect(0, 0, 128, 48, 8);
        ctx.fill();
        ctx.fillStyle = '#ffffff';
        ctx.font = 'bold 22px sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(text, 64, 24);

        const texture = new THREE.CanvasTexture(canvas);
        const spriteMat = new THREE.SpriteMaterial({ map: texture, depthTest: false });
        const sprite = new THREE.Sprite(spriteMat);
        sprite.position.copy(pos);
        sprite.scale.set(2, 0.75, 1);
        sprite.userData.isDimensionLabel = true;
        scene.add(sprite);
        dimensionLabels.push(sprite);
    });
}

function removeDimensionLabels() {
    dimensionLabels.forEach(sprite => scene.remove(sprite));
    dimensionLabels = [];
}

// ============================================
// MULTI-SELECT
// ============================================
let selectedObjects = new Set();

function toggleMultiSelect(obj) {
    if (selectedObjects.has(obj)) {
        selectedObjects.delete(obj);
        obj.traverse(child => {
            if (child.isMesh && child.material) {
                child.material.emissive = new THREE.Color(0x000000);
            }
        });
    } else {
        selectedObjects.add(obj);
        obj.traverse(child => {
            if (child.isMesh && child.material) {
                child.material.emissive = new THREE.Color(0x333333);
            }
        });
    }
    updateMultiSelectBadge();
    updateItemsList();
}

function clearMultiSelect() {
    selectedObjects.forEach(obj => {
        obj.traverse(child => {
            if (child.isMesh && child.material) {
                child.material.emissive = new THREE.Color(0x000000);
            }
        });
    });
    selectedObjects.clear();
    updateMultiSelectBadge();
}

function deleteMultiSelected() {
    if (selectedObjects.size === 0) return;

    saveState();

    selectedObjects.forEach(obj => {
        scene.remove(obj);
        furnitureObjects = furnitureObjects.filter(f => f !== obj);
    });

    const count = selectedObjects.size;
    selectedObjects.clear();
    selectedObject = null;

    document.getElementById('selectedControls').style.display = 'none';
    updateItemsList();
    updateStats();
    updateItemLimitBar();
    updateMultiSelectBadge();
    checkCollisions();

    showToast(`Removed ${count} items`, 'success');
}

function updateMultiSelectBadge() {
    let badge = document.getElementById('multiSelectBadge');
    if (selectedObjects.size > 0) {
        if (!badge) {
            badge = document.createElement('div');
            badge.id = 'multiSelectBadge';
            badge.className = 'multi-select-badge';
            document.querySelector('.simulator-container').appendChild(badge);
        }
        badge.innerHTML = `${selectedObjects.size} selected <button onclick="deleteMultiSelected()" class="multi-select-delete">🗑️ Delete</button>`;
        badge.style.display = 'flex';
    } else if (badge) {
        badge.style.display = 'none';
    }
}

// ============================================
// ANIMATION LOOP
// ============================================
function animate() {
    requestAnimationFrame(animate);
    renderer.render(scene, camera);
}

// ============================================
// WINDOW EXPORTS
// Expose functions to window for HTML onclick handlers
// ============================================
// Core UI functions
window.isPro = isPro;
window.showToast = showToast;
window.showModal = showModal;
window.closeModal = closeModal;
window.showPaywall = showPaywall;
window.hidePaywallToast = hidePaywallToast;

// Pro/upgrade functions
window.unlockPro = unlockPro;
window.updateProUI = updateProUI;
window.updateUpgradeButton = updateUpgradeButton;
window.getProPlanDetails = getProPlanDetails;
window.checkProStatus = checkProStatus;
window.handleRestoreEmailSubmit = handleRestoreEmailSubmit;
window.handleRestoreCodeSubmit = handleRestoreCodeSubmit;
window.restoreUseDifferentEmail = restoreUseDifferentEmail;
window.restorePurchase = restorePurchase;
window.purchasePro = purchasePro;

// Furniture functions
window.addFurnitureFromDatabase = addFurnitureFromDatabase;
window.addCustomItem = addCustomItem;
window.filterFurniture = filterFurniture;
window.renderFurnitureGrid = renderFurnitureGrid;

// Mobile UI toggles
window.toggleSearch = toggleSearch;
window.toggleCustomItem = toggleCustomItem;
window.dismissTouchHints = dismissTouchHints;

// Container functions
window.selectContainer = selectContainer;
window.renderContainerGrid = renderContainerGrid;

// Item manipulation functions
window.selectObjectByIndex = selectObjectByIndex;
window.deleteObjectByIndex = deleteObjectByIndex;
window.deleteSelected = deleteSelected;
window.duplicateSelected = duplicateSelected;
window.rotateSelected = rotateSelected;
window.flipSelected = flipSelected;
window.moveSelectedUp = moveSelectedUp;
window.moveSelectedDown = moveSelectedDown;
window.applyDimensionChanges = applyDimensionChanges;
window.updateVolumePreview = updateVolumePreview;
window.clearAll = clearAll;
window.undoAction = undoAction;
window.resetCamera = resetCamera;
window.toggleSnapToGrid = toggleSnapToGrid;
window.autoPack = autoPack;
window.deleteMultiSelected = deleteMultiSelected;

// Save/Load functions
window.saveProject = saveProject;
window.loadProject = loadProject;
window.saveToSlot = saveToSlot;
window.loadFromSlot = loadFromSlot;
window.deleteSaveSlot = deleteSaveSlot;
window.renderSaveSlots = renderSaveSlots;
window.exportProject = exportProject;
window.copyExportSummary = copyExportSummary;

// Payment functions
window.processPayment = processPayment;
window.formatCardNumber = formatCardNumber;
window.formatExpiry = formatExpiry;
window.formatCvc = formatCvc;

// ============================================
// START
// ============================================
window.addEventListener('load', init);
